import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const StepProgress = ({
	steps,
	currentStep,
}: {
	steps: { title: string }[];
	currentStep: number;
}) => {
	return (
		<div className="mb-10 w-100 overflow-hidden">
			<div
				className="d-flex align-items-center justify-content-between position-relative px-2 px-md-4"
				style={{ overflowX: "auto", paddingBottom: "10px" }}
			>
				{steps.map((step: any, index: number) => {
					const stepNumber = index + 1;
					const isCompleted = stepNumber < currentStep;
					const isActive = stepNumber === currentStep;
					const isPending = stepNumber > currentStep;

					return (
						<div
							key={index}
							className="d-flex align-items-center"
							style={{
								flex: index === steps.length - 1 ? "0 0 auto" : "1 1 auto",
							}}
						>
							{/* Step Content */}
							<div
								className="d-flex align-items-center justify-content-center p-2 rounded-pill"
								style={{
									minWidth: isActive ? "140px" : "auto",
									backgroundColor: isActive ? "#f8f9fc" : "transparent",
									border: isActive
										? "1px solid #e2e8f0"
										: "1px solid transparent",
									boxShadow: isActive
										? "0 4px 15px rgba(0, 0, 0, 0.05)"
										: "none",
									transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
									cursor: isCompleted ? "pointer" : "default",
									zIndex: 2,
								}}
							>
								{/* Indicator Icon/Circle */}
								<div
									className="rounded-circle d-flex align-items-center justify-content-center fw-bolder shadow-sm fs-5"
									style={{
										width: 46,
										height: 46,
										backgroundColor: isCompleted
											? "#10b981"
											: isActive
												? "#131c5d"
												: "#f1f5f9",
										color: isCompleted || isActive ? "#fff" : "#94a3b8",
										transition: "all 0.3s ease",
										flexShrink: 0,
									}}
								>
									{isCompleted ? (
										<svg
											width="22"
											height="22"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="3"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<polyline points="20 6 9 17 4 12"></polyline>
										</svg>
									) : (
										stepNumber
									)}
								</div>

								{/* Step Title (Show if Active or on larger screens for context) */}
								<div
									className={`ms-3 fw-bold ${isActive ? "text-dark d-block" : "text-muted d-none d-lg-block"}`}
									style={{
										fontSize: isActive ? "1.2rem" : "1.05rem",
										whiteSpace: "nowrap",
										transition: "opacity 0.3s ease",
										opacity: isActive ? 1 : 0.7,
									}}
								>
									{step.title}
								</div>
							</div>

							{/* Connecting Line (Don't show after last item) */}
							{index < steps.length - 1 && (
								<div
									className="flex-grow-1 mx-2 mx-md-4 position-relative"
									style={{ height: "2px", minWidth: "20px" }}
								>
									{/* Base Line */}
									<div
										className="position-absolute w-100 h-100"
										style={{ backgroundColor: "#e2e8f0", borderRadius: "1px" }}
									/>
									{/* Active Fill Line */}
									<div
										className="position-absolute h-100"
										style={{
											width: isCompleted ? "100%" : "0%",
											backgroundColor: "#10b981",
											borderRadius: "1px",
											transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
										}}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

import { CirclePlus, Trash2 } from "lucide-react";
import { KTCard, KTCardBody } from "../../../../_metronic/helpers";
import { useAuth } from "../../../modules/auth/core/Auth";
import { useCourses } from "../../course/hooks/useCourse";
import * as regApi from "../api/registration.api";
import { FileUpload } from "../helper/FileUpload";
import { PhotoUpload } from "../helper/PhotoUpload";
import {
	useBlocks,
	useBlocksList,
	useBloodGroups,
	useCastes,
	useDistricts,
	useGenders,
	useLevels,
	useQualifications,
	useRecruitmentTypes,
	useReligions,
	useRoles,
	useSchemesMaster,
	useUrbanBodyTypes,
	useVillages,
} from "../hooks/useMasterData";
import {
	useEmployeeById,
	useSaveEducationExperience,
	useSaveFamilyDetails,
	useSavePersonalDetails,
	useSaveTrainingAuditDetails,
	useSaveWorkingDetails,
} from "../hooks/useRegistration";
import {
	AcademicDetail,
	ComplaintDetail,
	ExperienceDetail,
	MasterItem,
	PilotAuditDetail,
	SkillDetail,
	StateOfficeDetail,
	TrainingDetail,
	WorkingDetail,
} from "../types/registration.type";

const FIELD_LABELS: Record<string, string> = {
	roleDesignation: "Role Designation",
	employeeCode: "Employee Code",
	district_id: "District",
	block_id: "Block",
	name: "Name",
	contact: "Contact Number",
	recruitmentType: "Recruitment Type",
	religion: "Religion",
	caste: "Caste",
	gender: "Gender",
	qualification: "Qualification",
	maritalStatus: "Marital Status",
	dob: "Date of Birth",
	age: "Age",
	bloodGroup: "Blood Group",
	differentlyAbled: "Differently Abled",
	healthIssues: "Health Issues",
	healthIssueDescription: "Health Issue Description",
	photo: "Profile Photo",
	disabilityCertificate: "Disability Certificate",
	healthCertificate: "Health Certificate",
	medicalCertificate: "Medical Certificate",
	areaType: "Area Type",
	district: "District",
	block: "Block",
	panchayat: "Panchayat",
	lgdCode: "LGD Code",
	urbanBodyType: "Urban Body Type",
	urbanBodyName: "Urban Body Name",
	fullAddress: "Full Address",
	pincode: "Pincode",
	fatherName: "Father Name",
	motherName: "Mother Name",
	spouseName: "Spouse Name",
	contactName: "Contact Person Name",
	emailId: "Email ID",
	eportalEmail: "E-Portal Email",
	pfmsId: "PFMS ID",
	bankName: "Bank Name",
	branchName: "Branch Name",
	accountNumber: "Account Number",
	retypeAccountNumber: "Retype Account Number",
	accountHolderName: "Account Holder Name",
	ifsc: "IFSC Code",
	bankPassbookFile: "Bank Passbook",
	aadhaarNumber: "Aadhaar Number",
	aadhaarFile: "Aadhaar File",
	panNumber: "PAN Number",
	panFile: "PAN File",
	uanNumber: "UAN Number",
	sparshNo: "Sparsh No",
};

/* ---------------- MAIN COMPONENT ---------------- */

const RegistrationFormPage = () => {
	const navigate = useNavigate();
	const { employeeId: paramId, id: registrationId } = useParams();
	const location = useLocation();
	const { currentUser } = useAuth();
	const isViewMode = location.pathname.includes("/view/");

	const {
		register,
		watch,
		setValue,
		getValues,
		handleSubmit,
		reset,
		trigger,
		setError,
		clearErrors,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		defaultValues: {
			photo: null,
			disabilityCertificate: null,
			healthCertificate: null,
			medicalCertificate: null,
			bankPassbookFile: null,
			aadhaarFile: null,
			panFile: null,
			uanNumber: "",
			sparshNo: "",
		},
	} as any);

	const district_id_step1 = watch("district_id");
	const district_id_step2 = watch("district");
	const photo = watch("photo");
	const roleDesignation = watch("roleDesignation");
	const areaType = watch("areaType");
	const dob = watch("dob");
	const workFrom = watch("workFrom");
	const workTo = watch("workTo");
	const differentlyAbled = watch("differentlyAbled");
	const healthIssues = watch("healthIssues");
	const block_id_step2 = watch("block");
	const panchayat_id = watch("panchayat");
	const hasWorkedAsDrpIc = watch("hasWorkedAsDrpIc");

	useEffect(() => {
		register("roleDesignation", { required: "Role Designation is required" });
		register("photo", { required: "Profile Photo is required" });
		register("disabilityCertificate", {
			required: "Disability certificate is required",
		});
		register("healthCertificate", {
			required: "Health certificate is required",
		});
		register("medicalCertificate", {
			required: "Medical certificate is required",
		});
		register("bankPassbookFile", { required: "Bank passbook is required" });
		register("aadhaarFile", { required: "Aadhaar file is required" });
		register("panFile", { required: "PAN file is required" });
	}, [register]);

	const [currentStep, setCurrentStep] = useState(1);
	const today = new Date().toISOString().split("T")[0];
	const maxDateLimit = new Date();
	maxDateLimit.setFullYear(maxDateLimit.getFullYear() - 18);
	const maxDateLimitStr = maxDateLimit.toISOString().split("T")[0];

	const calculateAge = (dobValue: string) => {
		const birthDate = new Date(dobValue);
		if (isNaN(birthDate.getTime())) return null;
		const today = new Date();
		let age = today.getFullYear() - birthDate.getFullYear();
		const m = today.getMonth() - birthDate.getMonth();
		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}
		return age;
	};

	const validateAge = (value: string) => {
		if (!value) return "Date of Birth is required";
		const selectedDate = new Date(value);
		const today = new Date();
		if (selectedDate > today) {
			return "Future dates are not allowed";
		}
		const age = calculateAge(value);
		if (age === null || age < 18) {
			return "Age must be 18 years or above";
		}
		return true;
	};

	const [childrenList, setChildrenList] = useState<
		{ name: string; aadhaar: string; occupation: string; workingStatus: string; educationStatus: string }[]
	>([]);

	const handleNumChildrenChange = (count: number) => {
		const newCount = Math.max(0, count);
		setChildrenList((prev) => {
			if (prev.length < newCount) {
				const toAdd = newCount - prev.length;
				const added = Array.from({ length: toAdd }, () => ({
					name: "",
					aadhaar: "",
					occupation: "",
					workingStatus: "Student",
					educationStatus: "School",
				}));
				return [...prev, ...added];
			} else {
				return prev.slice(0, newCount);
			}
		});
	};

	const handleAddChild = () => {
		setChildrenList((prev) => [
			...prev,
			{
				name: "",
				aadhaar: "",
				occupation: "",
				workingStatus: "Student",
				educationStatus: "School",
			},
		]);
	};

	const handleRemoveChild = (index: number) => {
		setChildrenList((prev) => prev.filter((_, i) => i !== index));
	};

	const handleChildChange = (index: number, field: string, value: string) => {
		setChildrenList((prev) =>
			prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
		);
	};

	const [employeeId, setEmployeeId] = useState<string | null>(paramId || null);
	const [loading, setLoading] = useState(false);
	const [academicList, setAcademicList] = useState<AcademicDetail[]>([
		{
			level: "",
			course: "",
			institution: "",
			board: "",
			from: "",
			to: "",
			percentage: "",
			certificate: null,
		},
	]);
	const [experienceList, setExperienceList] = useState<ExperienceDetail[]>([
		{
			company: "",
			jobNature: "",
			from: "",
			to: "",
			duration: "",
			certificate: null,
		},
	]);
	const [skillList, setSkillList] = useState<SkillDetail[]>([{ skill: "" }]);
	const [workingInfoList, setWorkingInfoList] = useState<WorkingDetail[]>([
		{
			station: "Worked",
			district: "",
			block: "",
			from: "",
			to: "",
			duration: "",
		},
	]);
	const [stateOfficeList, setStateOfficeList] = useState<StateOfficeDetail[]>([
		{ year: "", workParticulars: "" },
	]);
	const [drpIcWorkingInfoList, setDrpIcWorkingInfoList] = useState<
		WorkingDetail[]
	>([
		{
			station: "Worked",
			district: "",
			block: "",
			from: "",
			to: "",
			duration: "",
		},
	]);
	const [complaintList, setComplaintList] = useState<ComplaintDetail[]>([
		{
			receivedOn: "",
			complainantName: "",
			complaint: "",
			remarks: "",
			actionTaken: "",
		},
	]);
	const [trainingTakenList, setTrainingTakenList] = useState<TrainingDetail[]>([
		{ from: "", to: "", duration: "", location: "", name: "", grade: "" },
	]);
	const [trainingGivenList, setTrainingGivenList] = useState<TrainingDetail[]>([
		{ from: "", to: "", duration: "", location: "", name: "" },
	]);
	const [pilotAuditList, setPilotAuditList] = useState<PilotAuditDetail[]>([
		{ from: "", to: "", duration: "", location: "", scheme: "" },
	]);

	/* --------- DATA FETCHING FOR EDIT/VIEW --------- */
	const { employee: employeeData, isLoading: isLoadingEmployee } =
		useEmployeeById(employeeId || undefined);

	const isSelfProfile =
		!employeeId ||
		(Boolean(currentUser) &&
			Boolean(employeeData) &&
			(
				(employeeData.employee_code && (currentUser?.employee_code === employeeData.employee_code || (currentUser as any)?.username === employeeData.employee_code)) ||
				(employeeData.mobile_no && (currentUser?.mobile_no === employeeData.mobile_no || (currentUser as any)?.mobile === employeeData.mobile_no)) ||
				(employeeData.user_id && currentUser?.id === employeeData.user_id)
			));

	const redirectPath =
		roleDesignation === "2" ||
			registrationId === "2" ||
			employeeData?.type_id === 2
			? "/registration/state-officer"
			: "/registration";

	useEffect(() => {
		const fetchAndPopulate = async () => {
			if (!employeeData) return;
			const data = employeeData;
			try {
				// 1. Reset form with basic fields
				reset({
					roleDesignation: data.type_id?.toString(),
					employeeCode: data.employee_code,
					district_id: data.district_id?.toString(),
					block_id: data.block_id?.toString(),
					name: data.name,
					recruitmentType: data.recruitment_type_id?.toString(),
					contact: data.mobile_no,
					religion: data.religion_id?.toString(),
					caste: data.caste_id?.toString(),
					gender: data.gender_id?.toString(),
					qualification: data.qualification_id?.toString() || data.qualification?.toString() || "",
					maritalStatus: data.marital_status || "",
					dob: data.dob,
					age: data.age,
					bloodGroup: data.blood_group_id?.toString(),
					differentlyAbled: data.differently_abled === 1 ? "yes" : "no",
					healthIssues:
						data.health_status === 3
							? "major"
							: data.health_status === 2
								? "minor"
								: "normal",
					healthIssueDescription: data.type_issue,
					photo: data.photo_path,
					medicalCertificate: data.medical_certificate_path || "",
					healthCertificate: data.health_status_path || "",
					disabilityCertificate: data.differently_abled_path || "",

					// Step 2
					areaType: data.area_type === 1 ? "rural" : "urban",
					district: data.loca_district_id?.toString(),
					block: data.loca_block_id?.toString(),
					panchayat: data.village_id?.toString(),
					lgdCode: data.lgd_code,
					urbanBodyType: data.urban_body_type?.toString(),
					urbanBodyName: data.urban_body_name,
					fullAddress: data.address,
					pincode: data.pincode,
					fatherName: data.father_name,
					fatherAadhaar: data.family_details?.father?.aadhaar || "",
					fatherOccupation: data.family_details?.father?.occupation || "",
					fatherWorkingStatus: data.family_details?.father?.workingStatus || "Working",
					motherName: data.mother_name,
					motherAadhaar: data.family_details?.mother?.aadhaar || "",
					motherOccupation: data.family_details?.mother?.occupation || "",
					motherWorkingStatus: data.family_details?.mother?.workingStatus || "Working",
					spouseName: data.spouse_name,
					spouseAadhaar: data.family_details?.spouse?.aadhaar || "",
					spouseOccupation: data.family_details?.spouse?.occupation || "",
					spouseWorkingStatus: data.family_details?.spouse?.workingStatus || "Working",
					numChildren: Array.isArray(data.family_details?.children)
						? data.family_details.children.length
						: 0,
					contactName: data.contact_person,
					emailId: data.email,
					eportalEmail: data.eportal_email,
					pfmsId: data.pfms,
					bankName: data.bank_name,
					accountHolderName: data.account_holder_name,
					retypeAccountNumber: isSelfProfile
						? data.retype_account_no
						: data.account_no
							? "X".repeat(Math.max(0, (data.account_no || "").length - 4)) + (data.account_no || "").slice(-4)
							: "",
					branchName: data.branch_name,
					accountNumber: isSelfProfile
						? data.account_no
						: data.account_no
							? "X".repeat(Math.max(0, (data.account_no || "").length - 4)) + (data.account_no || "").slice(-4)
							: "",
					ifsc: data.ifsc_code,
					bankPassbookFile: data.bank_passbook_path,
					aadhaarNumber: data.aadhaar_no,
					aadhaarFile: data.aadhaar_upload_path,
					uanNumber: data.uan_no,
					sparshNo: data.sparsh_no || data.sparshNo || "",
					panNumber: data.pan_no,
					panFile: data.pan_upload_path,

					// Step 4
					joining_date: data.joining_date,

					// Step 5
					trainingTakenFrom: data.training_taken_details?.[0]?.from_date,
					trainingTakenTo: data.training_taken_details?.[0]?.to_date,
					trainingTakenLocation: data.training_taken_details?.[0]?.location,
					trainingTakenName: data.training_taken_details?.[0]?.training_name,
					trainingTakenGrade: data.training_taken_details?.[0]?.grade,

					trainingGivenFrom: data.training_given_details?.[0]?.from_date,
					trainingGivenTo: data.training_given_details?.[0]?.to_date,
					trainingGivenLocation: data.training_given_details?.[0]?.location,
					trainingGivenName: data.training_given_details?.[0]?.training_name,

					pilotAuditFrom: data.pilot_audit_details?.[0]?.from_date,
					pilotAuditTo: data.pilot_audit_details?.[0]?.to_date,
					pilotAuditLocation: data.pilot_audit_details?.[0]?.location,
					pilotAuditScheme:
						data.pilot_audit_details?.[0]?.scheme_name?.toString(),
				});

				// 2. Populate dynamic lists
				if (Array.isArray(data.family_details?.children)) {
					setChildrenList(data.family_details.children);
				}

				const academicDetails =
					data.academic_details?.map((a: any) => ({
						level: a.level_id?.toString(),
						course: a.course,
						institution: a.institution,
						board: a.board_university,
						from: a.from_year,
						to: a.to_year,
						percentage: a.aggregate,
						certificate: a.certificate_path,
					})) || [];
				setAcademicList(
					academicDetails.length > 0
						? academicDetails
						: [
							{
								level: "",
								course: "",
								institution: "",
								board: "",
								from: "",
								to: "",
								percentage: "",
								certificate: null,
							},
						],
				);

				const workExperience =
					data.work_experience?.map((e: any) => ({
						company: e.company,
						jobNature: e.nature_of_job,
						from: e.from_date || e.from_year,
						to: e.to_date || e.to_year,
						duration: e.duration,
						certificate: e.certificate_path,
					})) || [];
				setExperienceList(
					workExperience.length > 0
						? workExperience
						: [
							{
								company: "",
								jobNature: "",
								from: "",
								to: "",
								duration: "",
								certificate: null,
							},
						],
				);

				const skills =
					data.skills?.map((s: any) => ({ skill: s.skill || s })) || [];
				setSkillList(skills.length > 0 ? skills : [{ skill: "" }]);

				const workingInfo = data.brp_working_info || data.drp_work_detail;
				if (workingInfo && workingInfo.length > 0) {
					const populatedWorkingInfo = await Promise.all(
						workingInfo.map(async (w: any) => {
							const districtId = (
								w.brp_district_id || w.drp_district_id
							)?.toString();
							let blocks: MasterItem[] = [];
							if (districtId) {
								try {
									blocks = await regApi.fetchBlocks(districtId);
								} catch (error) {
									console.error(
										"Failed to fetch blocks for working info row during populate",
										error,
									);
								}
							}
							return {
								station:
									w.brp_station_id === 1 || w.drp_station_id === 1
										? "Worked"
										: "Present",
								district: districtId,
								block: (w.brp_block_id || w.drp_block_id)?.toString(),
								from: w.from_date,
								to: w.to_date,
								duration: w.duration,
								blocks: blocks,
							};
						}),
					);
					setWorkingInfoList(populatedWorkingInfo);
				} else {
					setWorkingInfoList([
						{
							station: "Worked",
							district: "",
							block: "",
							from: "",
							to: "",
							duration: "",
						},
					]);
				}

				if (data.drp_work_detail && data.drp_work_detail.length > 0) {
					setValue("hasWorkedAsDrpIc", data.worked_as_drp === 1 ? "yes" : "no");
					const populatedDrpIcWorkingInfo = await Promise.all(
						data.drp_work_detail.map(async (w: any) => {
							const districtId = w.drp_district_id?.toString();
							let blocks: MasterItem[] = [];
							if (districtId) {
								try {
									blocks = await regApi.fetchBlocks(districtId);
								} catch (error) {
									console.error(
										"Failed to fetch blocks for DRP I/C working info row during populate",
										error,
									);
								}
							}
							return {
								station: w.drp_station_id === 1 ? "Worked" : "Present",
								district: districtId,
								block: w.drp_block_id?.toString(),
								from: w.from,
								to: w.to,
								duration: w.duration,
								blocks: blocks,
							};
						}),
					);
					setDrpIcWorkingInfoList(populatedDrpIcWorkingInfo);
				} else {
					setValue("hasWorkedAsDrpIc", "no");
					setDrpIcWorkingInfoList([
						{
							station: "Worked",
							district: "",
							block: "",
							from: "",
							to: "",
							duration: "",
						},
					]);
				}

				const stateOfficeDetails =
					data.state_office_details?.map((s: any) => ({
						year: s.year,
						workParticulars: s.work_particulars,
					})) || [];
				setStateOfficeList(
					stateOfficeDetails.length > 0
						? stateOfficeDetails
						: [{ year: "", workParticulars: "" }],
				);

				const complaints =
					data.complaints?.map((c: any) => ({
						receivedOn: c.complaint_received_on,
						complainantName: c.complainant_name_contact,
						complaint: c.complaint_details,
						remarks: c.remarks,
						actionTaken: c.action_taken,
					})) || [];
				setComplaintList(
					complaints.length > 0
						? complaints
						: [
							{
								receivedOn: "",
								complainantName: "",
								complaint: "",
								remarks: "",
								actionTaken: "",
							},
						],
				);

				const trainingTaken =
					data.training_taken_details?.map((t: any) => ({
						from: t.from_date,
						to: t.to_date,
						duration: t.duration,
						location: t.location,
						name: t.training_name,
						grade: t.grade,
					})) || [];
				setTrainingTakenList(
					trainingTaken.length > 0
						? trainingTaken
						: [
							{
								from: "",
								to: "",
								duration: "",
								location: "",
								name: "",
								grade: "",
							},
						],
				);

				const trainingGiven =
					data.training_given_details?.map((t: any) => ({
						from: t.from_date,
						to: t.to_date,
						duration: t.duration,
						location: t.location,
						name: t.training_name,
					})) || [];
				setTrainingGivenList(
					trainingGiven.length > 0
						? trainingGiven
						: [{ from: "", to: "", duration: "", location: "", name: "" }],
				);

				const pilotAudit =
					data.pilot_audit_details?.map((p: any) => ({
						from: p.from_date,
						to: p.to_date,
						duration: p.duration,
						location: p.location,
						scheme: p.scheme_id?.toString(),
					})) || [];
				setPilotAuditList(
					pilotAudit.length > 0
						? pilotAudit
						: [{ from: "", to: "", duration: "", location: "", scheme: "" }],
				);
			} catch (err) {
				console.error("Population error", err);
				toast.error("Failed to load employee details.");
			}
		};
		fetchAndPopulate();
	}, [employeeData, reset]);

	/* --------- MASTER DATA STATE --------- */
	const { data: roleList = [] } = useRoles();
	const { data: genderList = [] } = useGenders();
	const { data: qualificationList = [] } = useQualifications();
	const { data: villageList = [] } = useVillages(block_id_step2);
	const { data: levelList = [] } = useLevels();
	const { courses: courseList = [] } = useCourses();
	const { data: urbanBodyTypeList = [] } = useUrbanBodyTypes();
	const { data: districtList = [] } = useDistricts();
	const { data: roleBlocks = [] } = useBlocksList();
	const { data: districtBlocks = [] } = useBlocks(district_id_step1);
	const step1Blocks =
		currentUser?.role_id?.toString() === "4" ? roleBlocks : districtBlocks;
	const { data: step2Blocks = [] } = useBlocks(district_id_step2);
	const { data: bloodGroupList = [] } = useBloodGroups();
	const { data: casteList = [] } = useCastes();
	const { data: recruitmentTypeList = [] } = useRecruitmentTypes();
	const { data: religionList = [] } = useReligions();
	const { data: schemeList = [] } = useSchemesMaster();

	const savePersonalMutation = useSavePersonalDetails();
	const saveFamilyMutation = useSaveFamilyDetails();
	const saveEducationMutation = useSaveEducationExperience();
	const saveWorkingMutation = useSaveWorkingDetails();
	const saveTrainingMutation = useSaveTrainingAuditDetails();

	useEffect(() => {
		if (panchayat_id && villageList.length > 0) {
			const selectedVillage = villageList.find(
				(v) => v.id.toString() === panchayat_id,
			);
			if (selectedVillage) {
				setValue("lgdCode", (selectedVillage as any).code || "");
				setValue("panchayat", panchayat_id); // Re-sync value
			}
		}
	}, [panchayat_id, villageList, setValue]);

	// Re-sync dropdowns to handle race conditions in edit mode
	useEffect(() => {
		const religion = getValues("religion");
		if (religion && religionList.length > 0) setValue("religion", religion);
	}, [religionList, setValue, getValues]);

	useEffect(() => {
		const caste = getValues("caste");
		if (caste && casteList.length > 0) setValue("caste", caste);
	}, [casteList, setValue, getValues]);

	useEffect(() => {
		const gender = getValues("gender");
		if (gender && genderList.length > 0) setValue("gender", gender);
	}, [genderList, setValue, getValues]);

	useEffect(() => {
		const bg = getValues("bloodGroup");
		if (bg && bloodGroupList.length > 0) setValue("bloodGroup", bg);
	}, [bloodGroupList, setValue, getValues]);

	useEffect(() => {
		const rt = getValues("recruitmentType");
		if (rt && recruitmentTypeList.length > 0) setValue("recruitmentType", rt);
	}, [recruitmentTypeList, setValue, getValues]);

	useEffect(() => {
		const bId = getValues("block_id");
		if (bId && step1Blocks.length > 0) {
			setValue("block_id", bId);
		}
	}, [step1Blocks, setValue, getValues]);

	useEffect(() => {
		const bId = getValues("block");
		if (bId && step2Blocks.length > 0) {
			setValue("block", bId);
		}
	}, [step2Blocks, setValue, getValues]);

	useEffect(() => {
		const dId = getValues("district_id");
		if (dId && districtList.length > 0) {
			setValue("district_id", dId);
		}
	}, [districtList, setValue, getValues]);

	useEffect(() => {
		if (currentUser) {
			// Auto_set IDs from login response if available
			if (
				(currentUser.role_id === 3 || currentUser.role_id === 4) &&
				!getValues("district_id")
			) {
				const dId = currentUser.district_id;
				if (dId) {
					setValue("district_id", dId.toString());
				}
				const bId = (currentUser as any).block_id;
				if (bId) {
					setValue("block_id", bId.toString());
				}
			}

			// Pre-fill basic info from login if empty
			if (
				!getValues("employeeCode") &&
				(currentUser.employee_code || (currentUser as any).username)
			) {
				setValue(
					"employeeCode",
					currentUser.employee_code || (currentUser as any).username,
				);
			}
			if (!getValues("name") && currentUser.name) {
				setValue("name", currentUser.name);
			}
			if (
				!getValues("contact") &&
				((currentUser as any).mobile || currentUser.mobile_no)
			) {
				setValue(
					"contact",
					(currentUser as any).mobile || currentUser.mobile_no,
				);
			}

			// Auto-set roleDesignation based on logged-in user's role if not already set (e.g., in Add mode)
			if (!getValues("roleDesignation")) {
				if (currentUser.role_id === 3) {
					setValue("roleDesignation", "4"); // DRP registers BRP
				} else if (currentUser.role_id === 4) {
					setValue("roleDesignation", "5"); // BRP registers VRP
				} else if (currentUser.role_id === 2) {
					setValue("roleDesignation", "2"); // State registers DRP (Special Role)
				}
			}
		}
	}, [currentUser, setValue, getValues]);

	const removeAcademic = (index: number) => {
		if (academicList.length <= 1) return;
		const updated = [...academicList];
		updated.splice(index, 1);
		setAcademicList(updated);
	};
	const removeExperience = (index: number) => {
		if (experienceList.length <= 1) return;
		const updated = [...experienceList];
		updated.splice(index, 1);
		setExperienceList(updated);
	};
	const removeSkill = (index: number) => {
		if (skillList.length <= 1) return;
		const updated = [...skillList];
		updated.splice(index, 1);
		setSkillList(updated);
	};

	const addWorkingInfo = () => {
		setWorkingInfoList([
			...workingInfoList,
			{
				station: "Worked",
				district: "",
				block: "",
				from: "",
				to: "",
				duration: "",
			},
		]);
	};

	const removeWorkingInfo = (index: number) => {
		if (workingInfoList.length <= 1) return;
		const updated = [...workingInfoList];
		updated.splice(index, 1);
		setWorkingInfoList(updated);
	};

	const calculateMonths = (from: string, to: string) => {
		if (!from || !to) return "";
		const startDate = new Date(from);
		const endDate = new Date(to);
		const months =
			(endDate.getFullYear() - startDate.getFullYear()) * 12 +
			(endDate.getMonth() - startDate.getMonth());
		return months >= 0 ? `${months} months` : "";
	};

	const updateWorkingDetail = async (
		index: number,
		field: keyof WorkingDetail,
		value: string,
	) => {
		if (
			field === "station" &&
			value === "Present" &&
			workingInfoList.some(
				(w, i) => i !== index && w.station === "Present",
			)
		) {
			toast.error(
				"Only one station can be marked as Present. Change the existing Present station first.",
			);
			return;
		}

		// The native date picker's max attribute only greys out future
		// dates in its own calendar widget — it does not stop a future
		// date being typed in directly or picked via some browsers'
		// pickers, so future dates must also be rejected here explicitly.
		if ((field === "from" || field === "to") && value > today) {
			toast.error("Future date is not allowed.");
			return;
		}

		if (
			field === "to" &&
			workingInfoList[index].from &&
			value < workingInfoList[index].from
		) {
			toast.error("To Date cannot be before From Date.");
			return;
		}

		if (
			field === "from" &&
			workingInfoList[index].to &&
			value > workingInfoList[index].to
		) {
			toast.error("From Date cannot be after To Date.");
			return;
		}

		const updated = [...workingInfoList];
		updated[index] = { ...updated[index], [field]: value };

		if (field === "district") {
			try {
				const blocks = await regApi.fetchBlocks(value);
				updated[index].blocks = blocks;
				updated[index].block = ""; // Reset block when district changes
			} catch (error) {
				console.error("Failed to fetch blocks for working info row", error);
			}
		}

		if (field === "station" && value === "Present") {
			updated[index].to = new Date().toISOString().split("T")[0];
			updated[index].duration = calculateMonths(
				updated[index].from,
				updated[index].to,
			);
		}

		if (field === "from" || field === "to") {
			updated[index].duration = calculateMonths(
				updated[index].from,
				updated[index].to,
			);
		}
		setWorkingInfoList(updated);
	};

	const updateExperienceDetail = (
		index: number,
		field: keyof ExperienceDetail,
		value: string,
	) => {
		const updated = [...experienceList];
		updated[index] = { ...updated[index], [field]: value };

		if (field === "from" || field === "to") {
			updated[index].duration = calculateMonths(
				updated[index].from,
				updated[index].to,
			);
		}
		setExperienceList(updated);
	};

	const updateAcademicDetail = (
		index: number,
		field: keyof AcademicDetail,
		value: string,
	) => {
		const updated = [...academicList];
		if (field === "level" && updated[index].level !== value) {
			updated[index] = { ...updated[index], level: value, course: "" };
		} else {
			updated[index] = { ...updated[index], [field]: value };
		}
		setAcademicList(updated);
	};

	const updateSkillDetail = (index: number, value: string) => {
		const updated = [...skillList];
		updated[index] = { skill: value };
		setSkillList(updated);
	};

	const addDrpIcWorkingInfo = () => {
		setDrpIcWorkingInfoList([
			...drpIcWorkingInfoList,
			{
				station: "Worked",
				district: "",
				block: "",
				from: "",
				to: "",
				duration: "",
			},
		]);
	};

	const removeDrpIcWorkingInfo = (index: number) => {
		if (drpIcWorkingInfoList.length <= 1) return;
		const updated = [...drpIcWorkingInfoList];
		updated.splice(index, 1);
		setDrpIcWorkingInfoList(updated);
	};

	const updateDrpIcWorkingDetail = async (
		index: number,
		field: keyof WorkingDetail,
		value: string,
	) => {
		if (
			field === "station" &&
			value === "Present" &&
			drpIcWorkingInfoList.some(
				(w, i) => i !== index && w.station === "Present",
			)
		) {
			toast.error(
				"Only one station can be marked as Present. Change the existing Present station first.",
			);
			return;
		}

		if ((field === "from" || field === "to") && value > today) {
			toast.error("Future date is not allowed.");
			return;
		}

		if (
			field === "to" &&
			drpIcWorkingInfoList[index].from &&
			value < drpIcWorkingInfoList[index].from
		) {
			toast.error("To Date cannot be before From Date.");
			return;
		}

		if (
			field === "from" &&
			drpIcWorkingInfoList[index].to &&
			value > drpIcWorkingInfoList[index].to
		) {
			toast.error("From Date cannot be after To Date.");
			return;
		}

		const updated = [...drpIcWorkingInfoList];
		updated[index] = { ...updated[index], [field]: value };

		if (field === "station" && value === "Present") {
			updated[index].to = today;
			updated[index].duration = calculateMonths(updated[index].from, today);
		}

		if (field === "district") {
			try {
				const blocks = await regApi.fetchBlocks(value);
				updated[index].blocks = blocks;
				updated[index].block = "";
			} catch (error) {
				console.error(
					"Failed to fetch blocks for DRP I/C working info row",
					error,
				);
			}
		}

		if (field === "from" || field === "to") {
			updated[index].duration = calculateMonths(
				updated[index].from,
				updated[index].to,
			);
		}
		setDrpIcWorkingInfoList(updated);
	};

	const addTrainingTaken = () => {
		setTrainingTakenList([
			...trainingTakenList,
			{ from: "", to: "", duration: "", location: "", name: "", grade: "" },
		]);
	};

	const removeTrainingTaken = (index: number) => {
		if (trainingTakenList.length <= 1) return;
		const updated = [...trainingTakenList];
		updated.splice(index, 1);
		setTrainingTakenList(updated);
	};

	const updateTrainingTaken = (
		index: number,
		field: keyof TrainingDetail,
		value: string,
	) => {
		const updated = [...trainingTakenList];
		updated[index] = { ...updated[index], [field]: value };
		if (field === "from" || field === "to") {
			updated[index].duration = calculateMonths(
				updated[index].from,
				updated[index].to,
			);
		}
		setTrainingTakenList(updated);
	};

	const addTrainingGiven = () => {
		setTrainingGivenList([
			...trainingGivenList,
			{ from: "", to: "", duration: "", location: "", name: "" },
		]);
	};

	const removeTrainingGiven = (index: number) => {
		if (trainingGivenList.length <= 1) return;
		const updated = [...trainingGivenList];
		updated.splice(index, 1);
		setTrainingGivenList(updated);
	};

	const updateTrainingGiven = (
		index: number,
		field: keyof TrainingDetail,
		value: string,
	) => {
		const updated = [...trainingGivenList];
		updated[index] = { ...updated[index], [field]: value };
		if (field === "from" || field === "to") {
			updated[index].duration = calculateMonths(
				updated[index].from,
				updated[index].to,
			);
		}
		setTrainingGivenList(updated);
	};

	const addPilotAudit = () => {
		setPilotAuditList([
			...pilotAuditList,
			{ from: "", to: "", duration: "", location: "", scheme: "" },
		]);
	};

	const removePilotAudit = (index: number) => {
		if (pilotAuditList.length <= 1) return;
		const updated = [...pilotAuditList];
		updated.splice(index, 1);
		setPilotAuditList(updated);
	};

	const updatePilotAudit = (
		index: number,
		field: keyof PilotAuditDetail,
		value: string,
	) => {
		const updated = [...pilotAuditList];
		updated[index] = { ...updated[index], [field]: value };
		if (field === "from" || field === "to") {
			updated[index].duration = calculateMonths(
				updated[index].from,
				updated[index].to,
			);
		}
		setPilotAuditList(updated);
	};

	const addStateOffice = () => {
		setStateOfficeList([...stateOfficeList, { year: "", workParticulars: "" }]);
	};

	const removeStateOffice = (index: number) => {
		if (stateOfficeList.length <= 1) return;
		const updated = [...stateOfficeList];
		updated.splice(index, 1);
		setStateOfficeList(updated);
	};

	const updateStateOfficeDetail = (
		index: number,
		field: keyof StateOfficeDetail,
		value: string,
	) => {
		const updated = [...stateOfficeList];
		updated[index] = { ...updated[index], [field]: value };
		setStateOfficeList(updated);
	};

	const addComplaint = () => {
		setComplaintList([
			...complaintList,
			{
				receivedOn: "",
				complainantName: "",
				complaint: "",
				remarks: "",
				actionTaken: "",
			},
		]);
	};

	const removeComplaint = (index: number) => {
		if (complaintList.length <= 1) return;
		const updated = [...complaintList];
		updated.splice(index, 1);
		setComplaintList(updated);
	};

	const updateComplaintDetail = (
		index: number,
		field: keyof ComplaintDetail,
		value: string,
	) => {
		const updated = [...complaintList];
		updated[index] = { ...updated[index], [field]: value };
		setComplaintList(updated);
	};

	const selectedRole = roleList.find(
		(r) => r.id.toString() === roleDesignation,
	);
	const isSpecialRole =
		currentUser?.role_id === 1 ||
		currentUser?.role_id === 2 ||
		currentUser?.role_id === 3 ||
		currentUser?.role_id === 4 ||
		roleDesignation === "2" ||
		roleDesignation === "3" ||
		roleDesignation === "4"; // ADMIN, STATE_OFFICER, DRP, or BRP

	const steps = [
		{ title: "Basic Information" },
		{ title: "Location & Identity" },
		{ title: "Academic & Experience" },
		...(isSpecialRole
			? [{ title: "Working Details" }, { title: "Training" }]
			: []),
	];
	const [trainingTab, setTrainingTab] = useState("taken");
	const [complainTab, setComplainTab] = useState("Complaints");
	const addAcademic = () => {
		setAcademicList([
			...academicList,
			{
				level: "",
				course: "",
				institution: "",
				board: "",
				from: "",
				to: "",
				percentage: "",
				certificate: null,
			},
		]);
	};

	const addExperience = () => {
		setExperienceList([
			...experienceList,
			{
				company: "",
				jobNature: "",
				from: "",
				to: "",
				duration: "",
				certificate: null,
			},
		]);
	};

	const addSkill = () => {
		setSkillList([...skillList, { skill: "" }]);
	};

	useEffect(() => {
		if (dob) {
			const birthDate = new Date(dob);
			const today = new Date();
			let age = today.getFullYear() - birthDate.getFullYear();
			const monthDiff = today.getMonth() - birthDate.getMonth();
			if (
				monthDiff < 0 ||
				(monthDiff === 0 && today.getDate() < birthDate.getDate())
			) {
				age--;
			}
			if (!isNaN(age)) {
				setValue("age", age);
			}
		}
	}, [dob, setValue]);

	/* --------- AUTO DURATION --------- */
	useEffect(() => {
		if (workFrom && workTo) {
			const from = new Date(workFrom);
			const to = new Date(workTo);
			const months =
				(to.getFullYear() - from.getFullYear()) * 12 +
				(to.getMonth() - from.getMonth());
			setValue("duration", `${months} months`);
		}
	}, [workFrom, workTo, setValue]);

	const nextStep = async () => {
		const formData = watch();

		// 1. Validate Fields for Current Step
		let fieldsToValidate: any[] = [];
		if (currentStep === 1) {
			fieldsToValidate = [
				"roleDesignation",
				"employeeCode",
				"name",
				"contact",
				"recruitmentType",
				"religion",
				"caste",
				"gender",
				"qualification",
				"maritalStatus",
				"dob",
				"differentlyAbled",
				"healthIssues",
				"photo",
			];
			if (roleDesignation === "3") fieldsToValidate.push("district_id"); // DRP
			if (roleDesignation === "4") {
				fieldsToValidate.push("district_id");
				if (currentUser?.role_id !== 3) fieldsToValidate.push("block_id");
			}
			if (roleDesignation === "5") {
				fieldsToValidate.push("district_id");
				if (currentUser?.role_id !== 3) fieldsToValidate.push("block_id");
			}
			if (differentlyAbled === "yes")
				fieldsToValidate.push("disabilityCertificate");
			if (healthIssues === "major")
				fieldsToValidate.push(
					"healthIssueDescription",
					"healthCertificate",
					"medicalCertificate",
				);
		} else if (currentStep === 2) {
			fieldsToValidate = [
				"areaType",
				"fullAddress",
				"pincode",
				"fatherName",
				"motherName",
				"emailId",
				"eportalEmail",
				"bankName",
				"branchName",
				"accountNumber",
				"retypeAccountNumber",
				"accountHolderName",
				"ifsc",
				"bankPassbookFile",
				"aadhaarNumber",
				"aadhaarFile",
				"panNumber",
				"panFile",
				"uanNumber",
			];
			if (areaType === "rural")
				fieldsToValidate.push("district", "block", "panchayat");
			if (areaType === "urban")
				fieldsToValidate.push("district", "urbanBodyType", "urbanBodyName");
		}

		if (!isViewMode) {
			let isStepValid = await trigger(fieldsToValidate);

			// Manual validation for files if trigger doesn't catch them
			if (currentStep === 2) {
				if (!formData.aadhaarFile) {
					setError("aadhaarFile", {
						type: "manual",
						message: "Aadhaar file is required",
					});
					isStepValid = false;
				} else {
					clearErrors("aadhaarFile");
				}

				if (!formData.panFile) {
					setError("panFile", {
						type: "manual",
						message: "PAN file is required",
					});
					isStepValid = false;
				} else {
					clearErrors("panFile");
				}

				if (!formData.bankPassbookFile) {
					setError("bankPassbookFile", {
						type: "manual",
						message: "Bank passbook is required",
					});
					isStepValid = false;
				} else {
					clearErrors("bankPassbookFile");
				}
			}

			if (!isStepValid) {
				if (
					currentStep === 2 &&
					errors.retypeAccountNumber?.type === "validate"
				) {
					toast.error("Account Numbers do not match");
				} else {
					const invalidFields = fieldsToValidate.filter((field) => {
						const val = formData[field];
						if (errors[field]) return true;
						if (val === null || val === undefined || val === "") return true;
						if (field === "photo" && !formData.photo) return true;
						if (field === "aadhaarFile" && !formData.aadhaarFile) return true;
						if (field === "panFile" && !formData.panFile) return true;
						if (field === "bankPassbookFile" && !formData.bankPassbookFile) return true;
						if (
							field === "disabilityCertificate" &&
							differentlyAbled === "yes" &&
							!formData.disabilityCertificate
						)
							return true;
						if (
							field === "healthCertificate" &&
							healthIssues === "major" &&
							!formData.healthCertificate
						)
							return true;
						if (
							field === "medicalCertificate" &&
							healthIssues === "major" &&
							!formData.medicalCertificate
						)
							return true;
						return false;
					});

					const fieldLabels = invalidFields
						.map((key) => FIELD_LABELS[key] || key)
						.filter(Boolean);
					const uniqueLabels = Array.from(new Set(fieldLabels));

					if (uniqueLabels.length > 0) {
						toast.error(`Please fill required fields: ${uniqueLabels.join(", ")}`);
					} else {
						toast.error("Please fill all required fields.");
					}
				}
				return;
			}
		}

		// Additional validation for dynamic lists in Step 3
		if (!isViewMode && currentStep === 3) {
			// Validation for Academic Details
			const hasValidAcademic = academicList.some(
				(a) =>
					a.level ||
					a.course ||
					a.institution ||
					a.board ||
					a.from ||
					a.to ||
					a.percentage,
			);
			if (!hasValidAcademic) {
				toast.error("Please add at least one Academic Detail.");
				return;
			}

			for (let i = 0; i < academicList.length; i++) {
				const a = academicList[i];
				if (a.from && a.to) {
					if (parseInt(a.to) < parseInt(a.from)) {
						toast.error(
							`Academic Record ${i + 1}: Year To cannot be earlier than Year From`,
						);
						return;
					}
				}
				// Optional: Ensure row is complete if any part is filled
				if (a.level || a.course || a.from || a.to) {
					if (!a.level || !a.course || !a.from || !a.to || !a.certificate) {
						toast.error(
							`Please complete all fields for Academic Record ${i + 1}`,
						);
						return;
					}
				}
			}

			// Validation for Work Experience
			const hasValidExperience = experienceList.some(
				(e) => e.company || e.jobNature || e.from || e.to,
			);
			if (!hasValidExperience) {
				toast.error("Please add at least one Work Experience.");
				return;
			}

			for (let i = 0; i < experienceList.length; i++) {
				const e = experienceList[i];
				if (e.from && e.to) {
					if (new Date(e.to) < new Date(e.from)) {
						toast.error(
							`Work Experience ${i + 1}: Work To cannot be earlier than Work From`,
						);
						return;
					}
				}
				if (e.company || e.jobNature || e.from || e.to) {
					if (
						!e.company ||
						!e.jobNature ||
						!e.from ||
						!e.to ||
						!e.certificate
					) {
						toast.error(
							`Please complete all fields for Work Experience ${i + 1}`,
						);
						return;
					}
				}
			}

			const hasValidSkill = skillList.some((s) => s.skill.trim() !== "");
			if (!hasValidSkill) {
				toast.error("Please add at least one Skill.");
				return;
			}
		}

		// 3. Additional validation for dynamic lists in Step 4
		if (
			!isViewMode &&
			currentStep === 4 &&
			(roleDesignation === "2" ||
				roleDesignation === "3" ||
				roleDesignation === "4")
		) {
			if (workingInfoList.length === 0) {
				toast.error("Please add at least one Working Info station.");
				return;
			}
			for (let i = 0; i < workingInfoList.length; i++) {
				const w = workingInfoList[i];
				if (
					!w.district ||
					!w.from ||
					!w.to ||
					(roleDesignation === "3" && !w.block)
				) {
					toast.error(
						`Please fill all required fields in Working Information Record ${i + 1}.`,
					);
					return;
				}
				if (new Date(w.to) < new Date(w.from)) {
					toast.error(
						`Working Information Record ${i + 1}: Work To cannot be earlier than Work From`,
					);
					return;
				}
			}

			if (roleDesignation === "3" && formData.hasWorkedAsDrpIc === "yes") {
				for (let i = 0; i < drpIcWorkingInfoList.length; i++) {
					const w = drpIcWorkingInfoList[i];
					if (!w.district || !w.from || !w.to || !w.block) {
						toast.error(
							`DRP IC Working Info Record ${i + 1}: Please fill all required fields.`,
						);
						return;
					}
					if (new Date(w.to) < new Date(w.from)) {
						toast.error(
							`DRP IC Working Info Record ${i + 1}: Work To cannot be earlier than Work From`,
						);
						return;
					}
				}
			}
		}

		if (isViewMode) {
			if (currentStep === steps.length) {
				navigate(redirectPath);
			} else {
				setCurrentStep((prev) => Math.min(prev + 1, steps.length));
			}
			return;
		}

		setLoading(true);
		try {
			if (currentStep === 1) {
				// Step 1: Personal Details (POST)
				const payload: any = {
					type_id: Number(formData.roleDesignation),
					district_id: ["2", "3", "4", "5"].includes(roleDesignation)
						? Number(formData.district_id)
						: null,
					block_id:
						["4", "5"].includes(roleDesignation) && currentUser?.role_id !== 3
							? Number(formData.block_id)
							: null,
					employee_code: formData.employeeCode,
					name: formData.name,
					recruitment_type_id: Number(formData.recruitmentType),
					mobile_no: formData.contact,
					religion_id: Number(formData.religion),
					caste_id: Number(formData.caste),
					gender_id: Number(formData.gender),
					qualification_id: Number(formData.qualification),
					marital_status: formData.maritalStatus,
					dob: formData.dob,
					age: Number(formData.age),
					blood_group_id: Number(formData.bloodGroup),
					differently_abled: formData.differentlyAbled === "yes" ? 1 : 2,
					health_status:
						formData.healthIssues === "major"
							? 3
							: formData.healthIssues === "minor"
								? 2
								: 1,
					type_issue: formData.healthIssueDescription || null,
					photo_path: formData.photo,
					differently_abled_path: formData.disabilityCertificate,
					health_status_path: formData.healthCertificate,
					medical_certificate_path: formData.medicalCertificate,
				};

				const response = await savePersonalMutation.mutateAsync({
					payload,
					id: employeeId || undefined,
				});
				const newId = response.data?.employeeId || response.data?.data?.id;
				if (newId) {
					setEmployeeId(newId);
					toast.success(
						response.data?.message ||
						"Step 1 Personal Details updated successfully",
					);
				} else {
					throw new Error("Failed to get Employee ID from Step 1");
				}
			} else if (employeeId) {
				if (currentStep === 2) {
					// Step 2: Family Details (PUT)
					const payload: any = {
						area_type: formData.areaType === "rural" ? 1 : 2,
						loca_district_id: Number(formData.district),
						loca_block_id: Number(formData.block),
						village_id: Number(formData.panchayat),
						lgd_code: formData.lgdCode,
						urban_body_type: Number(formData.urbanBodyType),
						urban_body_name: formData.urbanBodyName,
						address: formData.fullAddress,
						pincode: formData.pincode,
						father_name: formData.fatherName,
						mother_name: formData.motherName,
						spouse_name: formData.spouseName,
						family_details: {
							father: {
								aadhaar: formData.fatherAadhaar || "",
								occupation: formData.fatherOccupation || "",
								workingStatus: formData.fatherWorkingStatus || "Working",
							},
							mother: {
								aadhaar: formData.motherAadhaar || "",
								occupation: formData.motherOccupation || "",
								workingStatus: formData.motherWorkingStatus || "Working",
							},
							spouse: {
								aadhaar: formData.spouseAadhaar || "",
								occupation: formData.spouseOccupation || "",
								workingStatus: formData.spouseWorkingStatus || "Working",
							},
							children: childrenList,
						},
						contact_person: formData.contactName,
						email: formData.emailId,
						eportal_email: formData.eportalEmail,
						pfms: formData.pfmsId,
						bank_name: formData.bankName,
						account_holder_name: formData.accountHolderName,
						retype_account_no: (formData.accountNumber?.includes("X") || formData.retypeAccountNumber?.includes("X"))
							? (employeeData?.retype_account_no || employeeData?.account_no)
							: formData.retypeAccountNumber,
						branch_name: formData.branchName,
						account_no: (formData.accountNumber?.includes("X") || formData.retypeAccountNumber?.includes("X"))
							? employeeData?.account_no
							: formData.accountNumber,
						ifsc_code: formData.ifsc,
						bank_passbook_path: formData.bankPassbookFile,
						aadhaar_no: formData.aadhaarNumber,
						aadhaar_upload_path: formData.aadhaarFile,
						uan_no: formData.uanNumber,
						sparsh_no: formData.sparshNo || employeeData?.sparsh_no,
						pan_no: formData.panNumber,
						pan_upload_path: formData.panFile,
					};

					const response = await saveFamilyMutation.mutateAsync({
						id: employeeId,
						payload,
					});
					toast.success(
						response.data?.message ||
						"Step 2 Location & Identity updated successfully",
					);
				} else if (currentStep === 3) {
					// Step 3: Education & Experience (PUT)
					const hasValidAcademic =
						academicList.length > 0 &&
						academicList.some((a) => a.level || a.course);
					const hasValidExperience =
						experienceList.length > 0 &&
						experienceList.some((e) => e.company || e.jobNature);
					const hasValidSkill =
						skillList.length > 0 &&
						skillList.some((s) => s.skill.trim() !== "");

					if (!hasValidAcademic || !hasValidExperience || !hasValidSkill) {
						toast.error(
							"Please add at least one entry for Academic Details, Work Experience, and Skills.",
						);
						return;
					}

					const payload = {
						academic_details: academicList.map((a) => ({
							level_id: Number(a.level),
							course: a.course,
							institution: a.institution,
							board_university: a.board,
							from_year: a.from,
							to_year: a.to,
							aggregate: a.percentage,
							certificate_path: a.certificate, // Backend handles file
						})),
						work_experience: experienceList.map((e) => ({
							company: e.company,
							nature_of_job: e.jobNature,
							from_date: e.from,
							to_date: e.to,
							duration: e.duration,
							certificate_path: e.certificate, // Backend handles file
						})),
						skills: skillList.map((s) => s.skill).filter((s) => s !== ""),
					};
					const response = await saveEducationMutation.mutateAsync({
						id: employeeId,
						payload,
					});
					toast.success(
						response.data?.message ||
						"Step 3 Academic & Experience updated successfully",
					);
				} else if (currentStep === 4) {
					// Step 4: Working Details (PUT)
					const workingInfo = workingInfoList.map((w) => ({
						[["3", "4"].includes(roleDesignation)
							? "brp_station_id"
							: "drp_station_id"]: w.station === "Worked" ? 1 : 2,
						[["3", "4"].includes(roleDesignation)
							? "brp_district_id"
							: "drp_district_id"]: Number(w.district),
						[["3", "4"].includes(roleDesignation)
							? "brp_block_id"
							: "drp_block_id"]: Number(w.block),
						from_date: w.from,
						to_date: w.station === "Present" ? today : w.to,
						duration:
							w.station === "Present"
								? calculateMonths(w.from, today)
								: w.duration,
					}));

					const payload = {
						joining_date: formData.joining_date,
						[["3", "4"].includes(roleDesignation)
							? "brp_working_info"
							: "drp_work_detail"]: workingInfo,
						worked_as_drp:
							roleDesignation === "2" ||
								(["3", "4"].includes(roleDesignation) &&
									formData.hasWorkedAsDrpIc === "yes")
								? 1
								: 0,
						...(["3", "4"].includes(roleDesignation) && {
							drp_work_detail:
								formData.hasWorkedAsDrpIc === "yes"
									? drpIcWorkingInfoList.map((w) => ({
										drp_station_id: w.station === "Worked" ? 1 : 2,
										drp_district_id: Number(w.district),
										drp_block_id: Number(w.block),
										from: w.from,
										to: w.station === "Present" ? today : w.to,
										duration:
											w.station === "Present"
												? calculateMonths(w.from, today)
												: w.duration,
									}))
									: [],
						}),
					};
					const response = await saveWorkingMutation.mutateAsync({
						id: employeeId,
						payload,
					});
					toast.success(
						response.data?.message ||
						"Step 4 Working Details updated successfully",
					);
				}
			}
			setCurrentStep((prev) => Math.min(prev + 1, steps.length));
		} catch (error: any) {
			console.error("Failed to save step data", error);
			const errorMessage =
				error.response?.data?.message ||
				error.message ||
				"Failed to save data. Please check your inputs.";
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

	const onSubmit = async () => {
		if (!employeeId) {
			toast.error("Required data missing. Please complete previous steps.");
			return;
		}

		if (isViewMode) {
			navigate(redirectPath);
			return;
		}

		// Validate final step fields
		let fieldsToValidate: any[] = [];
		if (currentStep === 3) {
			// If already on 3 for non-special role, nextStep logic handles lists, but we trigger identity fields here if any
			fieldsToValidate = ["aadhaarNumber", "panNumber"];
		} else if (currentStep === 5) {
			fieldsToValidate = [
				// Individual fields are replaced by list checks below
			];
		}

		const isStepValid = await trigger(fieldsToValidate);
		if (!isStepValid) {
			toast.error("Please fix the errors before submitting.");
			return;
		}

		// List validations for Step 5
		if (!isViewMode && currentStep === 5) {
			// Training Taken Validation
			for (let i = 0; i < trainingTakenList.length; i++) {
				const t = trainingTakenList[i];
				if (t.from && t.to) {
					if (new Date(t.to) < new Date(t.from)) {
						toast.error(
							`Training Taken Record ${i + 1}: To Date cannot be earlier than From Date`,
						);
						return;
					}
				}
			}

			// Training Given Validation
			for (let i = 0; i < trainingGivenList.length; i++) {
				const t = trainingGivenList[i];
				if (t.from && t.to) {
					if (new Date(t.to) < new Date(t.from)) {
						toast.error(
							`Training Given Record ${i + 1}: To Date cannot be earlier than From Date`,
						);
						return;
					}
				}
			}

			// Pilot Audit Validation
			for (let i = 0; i < pilotAuditList.length; i++) {
				const p = pilotAuditList[i];
				if (p.from && p.to) {
					if (new Date(p.to) < new Date(p.from)) {
						toast.error(
							`Pilot Audit Record ${i + 1}: To Date cannot be earlier than From Date`,
						);
						return;
					}
				}
			}
		}

		if (currentStep === 3) {
			const hasValidAcademic = academicList.some(
				(a) =>
					a.level ||
					a.course ||
					a.institution ||
					a.board ||
					a.from ||
					a.to ||
					a.percentage,
			);
			const hasValidExperience = experienceList.some(
				(e) => e.company || e.jobNature || e.from || e.to,
			);
			const hasValidSkill = skillList.some((s) => s.skill.trim() !== "");

			if (!hasValidAcademic) {
				toast.error("Please add at least one Academic Detail.");
				return;
			}
			if (!hasValidExperience) {
				toast.error("Please add at least one Work Experience.");
				return;
			}
			if (!hasValidSkill) {
				toast.error("Please add at least one Skill.");
				return;
			}
		}

		setLoading(true);
		try {
			const formData = watch();

			// If final step is 3 (non-special role)
			if (currentStep === 3) {
				const payload = {
					academic_details: academicList.map((a) => ({
						level_id: Number(a.level),
						course: a.course,
						institution: a.institution,
						board_university: a.board,
						from_year: a.from,
						to_year: a.to,
						aggregate: a.percentage,
						certificate_path: a.certificate,
					})),
					work_experience: experienceList.map((e) => ({
						company: e.company,
						job_nature: e.jobNature,
						from_year: e.from,
						to_year: e.to,
						duration: e.duration,
						certificate_path: e.certificate,
					})),
					skills: skillList.map((s) => s.skill).filter((s) => s !== ""),
				};
				const response = await saveEducationMutation.mutateAsync({
					id: employeeId,
					payload,
				});
				toast.success(
					response.data?.message ||
					"Education & Experience updated successfully",
				);
				navigate(redirectPath);
			}

			// If final step is 5 (special role)
			if (currentStep === 5) {
				const payload = {
					training_taken_details: trainingTakenList.map((t) => ({
						from_date: t.from,
						to_date: t.to,
						duration: t.duration,
						location: t.location,
						training_name: t.name,
						grade: t.grade,
					})),
					training_given_details: trainingGivenList.map((t) => ({
						from_date: t.from,
						to_date: t.to,
						duration: t.duration,
						location: t.location,
						training_name: t.name,
					})),
					pilot_audit_details: pilotAuditList.map((p) => ({
						from_date: p.from,
						to_date: p.to,
						duration: p.duration,
						location: p.location,
						scheme_id: Number(p.scheme),
					})),
					state_office_details: stateOfficeList.map((s) => ({
						year: s.year,
						work_particulars: s.workParticulars,
					})),
					complaints: complaintList.map((c) => ({
						complaint_received_on: c.receivedOn,
						complainant_name_contact: c.complainantName,
						complaint_details: c.complaint,
						remarks: c.remarks,
						action_taken: c.actionTaken,
					})),
				};
				const response = await saveTrainingMutation.mutateAsync({
					id: employeeId,
					payload,
				});
				toast.success(
					response.data?.message || "Registration Completed Successfully",
				);
				navigate(redirectPath);
			}
		} catch (error: any) {
			console.error("Failed to complete registration", error);
			const errorMessage =
				error.response?.data?.message ||
				error.message ||
				"Failed to complete registration.";
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			className={`card glass-card shadow-sm border-0 ${isViewMode ? "is-view-mode" : ""}`}
		>
			<div className="card-header glass-effect">
				<h1 className="card-title fw-bold fs-2 ">Staff Registration Form</h1>
			</div>

			<div className="card-body p-10">
				<StepProgress steps={steps} currentStep={currentStep} />
				<form onSubmit={handleSubmit(onSubmit)}>
					{/* ================= STEP 1 ================= */}
					{currentStep === 1 && (
						<>
							{/* Role Designation removed as per requirement - now based on login response */}
							<h2 className="fw-bold mb-6 border-bottom pt-10 pb-2">
								Basic Information
							</h2>

							<div className="row">
								<div className="col-md-9">
									<div className="row">
										<div className="col-md-4 mb-5">
											<label className="form-label required fs-5 fw-bold text-dark">
												Employee Code
											</label>
											<input
												{...register("employeeCode", {
													required: "Employee Code is required",
												})}
												disabled={true}
												className={`form-control ${errors.employeeCode ? "is-invalid" : ""}`}
											/>
											{errors.employeeCode && (
												<div className="invalid-feedback">
													{errors.employeeCode.message as string}
												</div>
											)}
										</div>
										{roleDesignation === "2" && (
											<div className="col-md-4 mb-5">
												<label className="form-label required fs-5 fw-bold text-dark">
													State
												</label>
												<input
													type="text"
													value="Tamil Nadu"
													disabled
													className="form-control bg-light"
												/>
											</div>
										)}
										{["3", "4", "5"].includes(roleDesignation) && (
											<div className="col-md-4 mb-5">
												<label className="form-label required fs-5 fw-bold text-dark">
													District
												</label>
												<select
													{...register("district_id", {
														required: "District is required",
														onChange: (e) => {
															if (
																!["3", "4"].includes(
																	currentUser?.role_id?.toString() || "",
																)
															) {
																setValue("block_id", "");
															}
														},
													})}
													disabled={
														isViewMode ||
														currentUser?.role_id !== 1 ||
														["3", "4"].includes(
															currentUser?.role_id?.toString() || "",
														)
													}
													className={`form-select ${errors.district_id ? "is-invalid" : ""}`}
												>
													<option value="">Select District</option>
													{districtList.map((item) => (
														<option key={item.id} value={item.id.toString()}>
															{item.name}
														</option>
													))}
												</select>
												{errors.district_id && (
													<div className="invalid-feedback">
														{errors.district_id.message as string}
													</div>
												)}
											</div>
										)}

										{["4", "5"].includes(roleDesignation) &&
											currentUser?.role_id !== 3 && (
												<div className="col-md-4 mb-5">
													<label className="form-label required fs-5 fw-bold text-dark">
														Block
													</label>
													<select
														{...register("block_id", {
															required: "Block is required",
														})}
														disabled={
															isViewMode ||
															currentUser?.role_id !== 1 ||
															["4"].includes(
																currentUser?.role_id?.toString() || "",
															)
														}
														className={`form-select ${errors.block_id ? "is-invalid" : ""}`}
													>
														<option value="">Select Block</option>
														{step1Blocks.map((item) => (
															<option key={item.id} value={item.id.toString()}>
																{item.name}
															</option>
														))}
													</select>
													{errors.block_id && (
														<div className="invalid-feedback">
															{errors.block_id.message as string}
														</div>
													)}
												</div>
											)}

										<div className="col-md-4 mb-5">
											<label className="form-label required fs-5 fw-bold text-dark">
												Name
											</label>
											<input
												{...register("name", {
													required: "Name is required",
													pattern: {
														value: /^[a-zA-Z\s]*$/,
														message:
															"Name can only contain alphabets and spaces",
													},
													onChange: (e) => {
														e.target.value = e.target.value.replace(
															/[^a-zA-Z\s]/g,
															"",
														);
													},
												})}
												disabled={isViewMode || currentUser?.role_id !== 1}
												className={`form-control ${errors.name ? "is-invalid" : ""}`}
											/>
											{errors.name && (
												<div className="invalid-feedback">
													{errors.name.message as string}
												</div>
											)}
										</div>

										<div className="col-md-4 mb-5">
											<label className="form-label required fs-5 fw-bold text-dark">
												Contact Number
											</label>
											<input
												{...register("contact", {
													required: "Contact Number is required",
													pattern: {
														value: /^[0-9]{10}$/,
														message: "Mobile number must be exactly 10 digits",
													},
													onChange: (e) => {
														e.target.value = e.target.value.replace(
															/[^0-9]/g,
															"",
														);
													},
												})}
												disabled={isViewMode || currentUser?.role_id !== 1}
												maxLength={10}
												className={`form-control ${errors.contact ? "is-invalid" : ""}`}
											/>
											{errors.contact && (
												<div className="invalid-feedback">
													{errors.contact.message as string}
												</div>
											)}
										</div>

										<div className="col-md-4 mb-5">
											<label className="form-label required fs-5 fw-bold text-dark">
												Recruitment Type
											</label>
											<select
												{...register("recruitmentType", {
													required: "Recruitment Type is required",
												})}
												disabled={isViewMode}
												className={`form-select ${errors.recruitmentType ? "is-invalid" : ""}`}
											>
												<option value="">Select</option>
												{recruitmentTypeList.map((item) => (
													<option key={item.id} value={item.id.toString()}>
														{item.name}
													</option>
												))}
											</select>
											{errors.recruitmentType && (
												<div className="invalid-feedback">
													{errors.recruitmentType.message as string}
												</div>
											)}
										</div>
									</div>
								</div>
								<div className="col-md-3 mb-5 d-flex flex-column align-items-center">
									<PhotoUpload
										label="Profile Photo"
										required
										isInvalid={!!errors.photo}
										value={photo}
										disabled={isViewMode}
										onChange={(file) =>
											setValue("photo", file, { shouldValidate: true })
										}
										onUpload={regApi.uploadProfilePhoto}
									/>
									{errors.photo && (
										<div className="text-danger d-block mt-1 text-center fw-bold fs-7">
											{errors.photo.message as string}
										</div>
									)}
								</div>
							</div>

							<h2 className="fw-bold mb-6 border-top border-bottom pt-5 pb-5">
								Personal Details
							</h2>

							<div className="row">
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Religion
									</label>
									<select
										{...register("religion", {
											required: "Religion is required",
										})}
										disabled={isViewMode}
										className={`form-select ${errors.religion ? "is-invalid" : ""}`}
									>
										<option value="">Select Religion</option>
										{religionList.map((item) => (
											<option key={item.id} value={item.id.toString()}>
												{item.name}
											</option>
										))}
									</select>
									{errors.religion && (
										<div className="invalid-feedback">
											{errors.religion.message as string}
										</div>
									)}
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Caste
									</label>
									<select
										{...register("caste", { required: "Caste is required" })}
										disabled={isViewMode}
										className={`form-select ${errors.caste ? "is-invalid" : ""}`}
									>
										<option value="">Select Caste</option>
										{casteList.map((item) => (
											<option key={item.id} value={item.id.toString()}>
												{item.name}
											</option>
										))}
									</select>
									{errors.caste && (
										<div className="invalid-feedback">
											{errors.caste.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Gender
									</label>
									<select
										{...register("gender", {
											required: "Gender is required",
										})}
										disabled={isViewMode}
										className={`form-select ${errors.gender ? "is-invalid" : ""}`}
									>
										<option value="">Select Gender</option>
										{(genderList.length > 0
											? genderList
											: [
												{ id: 1, name: "Male" },
												{ id: 2, name: "Female" },
												{ id: 3, name: "Transgender / Other" },
											]
										).map((item) => (
											<option key={item.id} value={item.id.toString()}>
												{item.name}
											</option>
										))}
									</select>
									{errors.gender && (
										<div className="invalid-feedback">
											{errors.gender.message as string}
										</div>
									)}
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Qualification
									</label>
									<select
										{...register("qualification", {
											required: "Qualification is required",
										})}
										disabled={isViewMode}
										className={`form-select ${errors.qualification ? "is-invalid" : ""}`}
									>
										<option value="">Select Qualification</option>
										{(qualificationList.length > 0
											? qualificationList
											: [
												{ id: 1, name: "10th / SSLC" },
												{ id: 2, name: "12th / Higher Secondary" },
												{ id: 3, name: "ITI / Diploma" },
												{ id: 4, name: "Graduate (UG)" },
												{ id: 5, name: "Post Graduate (PG)" },
												{ id: 6, name: "Doctorate (Ph.D)" },
												{ id: 7, name: "Others" },
											]
										).map((item: any) => (
											<option key={item.id} value={item.id.toString()}>
												{item.name}
											</option>
										))}
									</select>
									{errors.qualification && (
										<div className="invalid-feedback">
											{errors.qualification.message as string}
										</div>
									)}
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Marital Status
									</label>
									<select
										{...register("maritalStatus", {
											required: "Marital Status is required",
										})}
										disabled={isViewMode}
										className={`form-select ${errors.maritalStatus ? "is-invalid" : ""}`}
									>
										<option value="">Select Marital Status</option>
										<option value="Single">Single</option>
										<option value="Married">Married</option>
										<option value="Divorced">Divorced</option>
									</select>
									{errors.maritalStatus && (
										<div className="invalid-feedback">
											{errors.maritalStatus.message as string}
										</div>
									)}
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Date of Birth
									</label>
									<input
										type="date"
										{...register("dob", {
											required: "Date of Birth is required",
											validate: validateAge,
											onChange: (e) => {
												const age = calculateAge(e.target.value);
												setValue("age", age !== null ? age : "");
											},
										})}
										max={maxDateLimitStr}
										disabled={isViewMode}
										className={`form-control ${errors.dob ? "is-invalid" : ""}`}
										placeholder="Pick a date"
									/>
									{errors.dob && (
										<div className="invalid-feedback">
											{errors.dob.message as string}
										</div>
									)}
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Age
									</label>
									<input
										{...register("age")}
										readOnly
										disabled={isViewMode}
										className="form-control bg-light"
									/>
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Blood Group
									</label>
									<select
										{...register("bloodGroup")}
										disabled={isViewMode}
										className="form-select"
									>
										<option value="">Select Blood Group</option>
										{bloodGroupList.map((item) => (
											<option key={item.id} value={item.id.toString()}>
												{item.name}
											</option>
										))}
									</select>
								</div>
							</div>

							<div className="mt-8 mb-4">
								<h5 className="text-dark fw-bold mb-5">Health Related</h5>
								<div>
									<div className="row mb-7 align-items-center gap-5">
										<div
											className={
												differentlyAbled === "yes" ? "col-auto" : "col-md-12"
											}
										>
											<label className="form-label required d-block mb-3 fs-5 fw-bold text-dark">
												Differently Abled?
											</label>
											<div className="d-flex gap-10">
												<div className="form-check form-check-custom form-check-solid">
													<input
														className="form-check-input"
														type="radio"
														value="yes"
														disabled={isViewMode}
														{...register("differentlyAbled", {
															required: "Please select an option",
														})}
														id="abled_yes"
													/>
													<label
														className="form-check-label ms-3"
														htmlFor="abled_yes"
													>
														Yes
													</label>
												</div>
												<div className="form-check form-check-custom form-check-solid">
													<input
														className="form-check-input"
														type="radio"
														value="no"
														disabled={isViewMode}
														{...register("differentlyAbled", {
															required: "Please select an option",
														})}
														id="abled_no"
													/>
													<label
														className="form-check-label ms-3"
														htmlFor="abled_no"
													>
														No
													</label>
												</div>
											</div>
											{errors.differentlyAbled && (
												<div className="text-danger fs-7 mt-2">
													{errors.differentlyAbled.message as string}
												</div>
											)}
										</div>

										{differentlyAbled === "yes" && (
											<div className="col-md-4">
												<FileUpload
													label="Disability Certificate *"
													value={watch("disabilityCertificate")}
													isInvalid={!!errors.disabilityCertificate}
													disabled={isViewMode}
													onChange={(file) =>
														setValue("disabilityCertificate", file, {
															shouldValidate: true,
														})
													}
													onUpload={regApi.uploadDisabilityCertificate}
													maxSizeMB={5}
													description="Max 5MB (PDF/JPG/PNG)"
												/>
												{errors.disabilityCertificate && (
													<div className="text-danger d-block mt-1 fw-bold fs-7">
														{errors.disabilityCertificate.message as string}
													</div>
												)}
											</div>
										)}
									</div>
								</div>

								<div className="mb-7">
									<label className="form-label required d-block mb-3 fs-5 fw-bold text-dark">
										Health Issues?
									</label>
									<div className="d-flex gap-10">
										<div className="form-check form-check-custom form-check-solid">
											<input
												className="form-check-input"
												type="radio"
												value="normal"
												disabled={isViewMode}
												{...register("healthIssues", {
													required: "Please select an option",
												})}
												id="health_normal"
											/>
											<label
												className="form-check-label ms-3"
												htmlFor="health_normal"
											>
												Normal
											</label>
										</div>
										<div className="form-check form-check-custom form-check-solid">
											<input
												className="form-check-input"
												type="radio"
												value="minor"
												disabled={isViewMode}
												{...register("healthIssues", {
													required: "Please select an option",
												})}
												id="health_minor"
											/>
											<label
												className="form-check-label ms-3"
												htmlFor="health_minor"
											>
												Minor
											</label>
										</div>
										<div className="form-check form-check-custom form-check-solid">
											<input
												className="form-check-input"
												type="radio"
												value="major"
												disabled={isViewMode}
												{...register("healthIssues", {
													required: "Please select an option",
												})}
												id="health_major"
											/>
											<label
												className="form-check-label ms-3"
												htmlFor="health_major"
											>
												Major
											</label>
										</div>
									</div>
									{errors.healthIssues && (
										<div className="text-danger fs-7 mt-2">
											{errors.healthIssues.message as string}
										</div>
									)}
								</div>
							</div>

							{(healthIssues === "minor" || healthIssues === "major") && (
								<KTCard className="shadow-sm border-1 border-primary mt-10 mb-7">
									<KTCardBody className="p-8">
										<h4 className="fw-bold mb-8">
											Health Related & Certificates
										</h4>

										<div className="row">
											{(healthIssues === "minor" ||
												healthIssues === "major") && (
													<div className="col-md-6 mb-5">
														<label
															className={`form-label ${healthIssues === "major" ? "required" : ""} fw-bold`}
														>
															Type of issues
														</label>
														<textarea
															{...register("healthIssueDescription", {
																required:
																	healthIssues === "major"
																		? "Description is required for major issues"
																		: false,
															})}
															disabled={isViewMode}
															className={`form-control ${errors.healthIssueDescription ? "is-invalid" : ""}`}
															rows={2}
															placeholder="Describe the health issues or disability..."
														/>
														{errors.healthIssueDescription && (
															<div className="invalid-feedback">
																{errors.healthIssueDescription.message as string}
															</div>
														)}
													</div>
												)}

											{(healthIssues === "minor" ||
												healthIssues === "major") && (
													<div className="col-md-6 mb-5">
														<FileUpload
															label="Health Certificate"
															required={healthIssues === "major"}
															isInvalid={!!errors.healthCertificate}
															value={watch("healthCertificate")}
															disabled={isViewMode}
															onChange={(file) =>
																setValue("healthCertificate", file, {
																	shouldValidate: true,
																})
															}
															onUpload={regApi.uploadHealthCertificate}
															maxSizeMB={5}
															description="Max 5MB (PDF/JPG/PNG)"
														/>
														{errors.healthCertificate && (
															<div className="text-danger d-block mt-1 fw-bold fs-7">
																{errors.healthCertificate.message as string}
															</div>
														)}
													</div>
												)}

											{healthIssues === "major" && (
												<div className="col-md-6 mb-5">
													<FileUpload
														label="Medical Certificate"
														required
														isInvalid={!!errors.medicalCertificate}
														value={watch("medicalCertificate")}
														disabled={isViewMode}
														onChange={(file) =>
															setValue("medicalCertificate", file, {
																shouldValidate: true,
															})
														}
														onUpload={regApi.uploadMedicalCertificate}
														maxSizeMB={5}
														description="Max 5MB (PDF/JPG/PNG)"
													/>
													{errors.medicalCertificate && (
														<div className="text-danger d-block mt-1 fw-bold fs-7">
															{errors.medicalCertificate.message as string}
														</div>
													)}
												</div>
											)}
										</div>
									</KTCardBody>
								</KTCard>
							)}
						</>
					)}

					{/* ================= STEP 2 ================= */}
					{currentStep === 2 && (
						<>
							<h2 className="fw-bold mb-6 border-bottom pb-2">
								Location Details
							</h2>

							<div className="row">
								<div className="mb-7">
									<label className="form-label required text-dark fw-bold mb-5">
										Area Type
									</label>
									<div className="d-flex gap-10">
										<div className="form-check form-check-custom form-check-solid">
											<input
												className="form-check-input"
												type="radio"
												value="rural"
												disabled={isViewMode}
												{...register("areaType", {
													required: "Please select an Area Type",
												})}
												id="areaType_rural"
											/>
											<label
												className="form-check-label ms-3"
												htmlFor="areaType_rural"
											>
												Rural
											</label>
										</div>
										<div className="form-check form-check-custom form-check-solid">
											<input
												className="form-check-input"
												type="radio"
												value="urban"
												disabled={isViewMode}
												{...register("areaType", {
													required: "Please select an Area Type",
												})}
												id="areaType_urban"
											/>
											<label
												className="form-check-label ms-3"
												htmlFor="areaType_urban"
											>
												Urban
											</label>
										</div>
									</div>
									{errors.areaType && (
										<div className="text-danger fs-7 mt-2">
											{errors.areaType.message as string}
										</div>
									)}
								</div>
								{/* Rural */}
								{areaType === "rural" && (
									<>
										<h5 className="fw-bold mt-6 mb-4">Rural Type Details</h5>
										<div className="row">
											<div className="col-md-4 mb-5">
												<label className="form-label required fs-5 fw-bold text-dark">
													District
												</label>
												<select
													className={`form-select ${errors.district ? "is-invalid" : ""}`}
													{...register("district", {
														required: "District is required",
														onChange: (e) => {
															setValue("block", "");
															setValue("panchayat", "");
															setValue("lgdCode", "");
														},
													})}
													disabled={isViewMode}
												>
													<option value="">Select District</option>
													{districtList.map((item) => (
														<option key={item.id} value={item.id.toString()}>
															{item.name}
														</option>
													))}
												</select>
												{errors.district && (
													<div className="invalid-feedback">
														{errors.district.message as string}
													</div>
												)}
											</div>
											<div className="col-md-4 mb-5">
												<label className="form-label required fs-5 fw-bold text-dark">
													Block
												</label>
												<select
													{...register("block", {
														required:
															areaType === "rural"
																? "Block is required"
																: false,
													})}
													disabled={isViewMode}
													className={`form-select ${errors.block ? "is-invalid" : ""}`}
												>
													<option value="">Select Block</option>
													{step2Blocks.map((item) => (
														<option key={item.id} value={item.id.toString()}>
															{item.name}
														</option>
													))}
												</select>
												{errors.block && (
													<div className="invalid-feedback">
														{errors.block.message as string}
													</div>
												)}
											</div>
											<div className="col-md-4 mb-5">
												<label className="form-label required fs-5 fw-bold text-dark">
													Panchayat
												</label>
												<select
													{...register("panchayat", {
														required:
															areaType === "rural"
																? "Panchayat is required"
																: false,
													})}
													disabled={isViewMode}
													className={`form-select ${errors.panchayat ? "is-invalid" : ""}`}
												>
													<option value="">Select Panchayat</option>
													{villageList.map((item) => (
														<option key={item.id} value={item.id.toString()}>
															{item.name}
														</option>
													))}
												</select>
												{errors.panchayat && (
													<div className="invalid-feedback">
														{errors.panchayat.message as string}
													</div>
												)}
											</div>
											<div className="col-md-4 mb-5">
												<label className="form-label fs-5 fw-bold text-dark">
													LGD Code
												</label>
												<input
													{...register("lgdCode")}
													readOnly
													className={`form-control bg-light ${errors.lgdCode ? "is-invalid" : ""}`}
												/>
												{errors.lgdCode && (
													<div className="invalid-feedback">
														{errors.lgdCode.message as string}
													</div>
												)}
											</div>
										</div>
									</>
								)}

								{/* Urban */}
								{areaType === "urban" && (
									<>
										<h5 className="fw-bold mt-6 mb-4">Urban Type Details</h5>
										<div className="col-md-4 mb-5">
											<label className="form-label required fs-5 fw-bold text-dark">
												District
											</label>
											<select
												{...register("district", {
													required: "District is required",
													onChange: (e) => {
														setValue("urbanBodyType", "");
														setValue("urbanBodyName", "");
													},
												})}
												disabled={isViewMode}
												className={`form-select ${errors.district ? "is-invalid" : ""}`}
											>
												<option value="">Select District</option>
												{districtList.map((item) => (
													<option key={item.id} value={item.id.toString()}>
														{item.name}
													</option>
												))}
											</select>
											{errors.district && (
												<div className="invalid-feedback">
													{errors.district.message as string}
												</div>
											)}
										</div>
										<div className="col-md-4 mb-5">
											<label className="form-label required fs-5 fw-bold text-dark">
												Urban Body Type
											</label>
											<select
												{...register("urbanBodyType", {
													required:
														areaType === "urban"
															? "Urban Body Type is required"
															: false,
												})}
												disabled={isViewMode}
												className={`form-select ${errors.urbanBodyType ? "is-invalid" : ""}`}
											>
												<option value="">Select Type</option>
												{urbanBodyTypeList.map((item) => (
													<option key={item.id} value={item.id.toString()}>
														{item.name}
													</option>
												))}
											</select>
											{errors.urbanBodyType && (
												<div className="invalid-feedback">
													{errors.urbanBodyType.message as string}
												</div>
											)}
										</div>
										<div className="col-md-4 mb-5">
											<label className="form-label required fs-5 fw-bold text-dark">
												Urban Body Name
											</label>
											<input
												{...register("urbanBodyName", {
													required:
														areaType === "urban"
															? "Urban Body Name is required"
															: false,
												})}
												disabled={isViewMode}
												className={`form-control ${errors.urbanBodyName ? "is-invalid" : ""}`}
											/>
											{errors.urbanBodyName && (
												<div className="invalid-feedback">
													{errors.urbanBodyName.message as string}
												</div>
											)}
										</div>
									</>
								)}
								<div className="col-md-8 mb-5">
									<label className="form-label required fw-bold">
										Full Address (as per Aadhaar)
									</label>
									<textarea
										{...register("fullAddress", {
											required: "Full Address is required",
										})}
										disabled={isViewMode}
										className={`form-control ${errors.fullAddress ? "is-invalid" : ""}`}
										rows={6}
										placeholder="Enter Full Address"
									/>
									{errors.fullAddress && (
										<div className="invalid-feedback">
											{errors.fullAddress.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Pincode
									</label>
									<input
										{...register("pincode", {
											required: "Pincode is required",
											pattern: {
												value: /^6[0-9]{5}$/,
												message: "Invalid Pincode (Must start with 6)",
											},
											maxLength: {
												value: 6,
												message: "Pincode must be 6 digits",
											},
										})}
										disabled={isViewMode}
										maxLength={6}
										className={`form-control ${errors.pincode ? "is-invalid" : ""}`}
									/>
									{errors.pincode && (
										<div className="invalid-feedback">
											{errors.pincode.message as string}
										</div>
									)}
								</div>
							</div>

							<h2 className="fw-bold mb-6 border-top border-bottom pt-5 pb-5">
								Family Details
							</h2>

							<div className="row">
								{/* Father Details */}
								<div className="col-md-3 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Father's Name
									</label>
									<input
										{...register("fatherName", {
											required: "Father's Name is required",
											pattern: {
												value: /^[a-zA-Z\s]*$/,
												message: "Only alphabets are allowed",
											},
											onChange: (e) => {
												e.target.value = e.target.value.replace(
													/[^a-zA-Z\s]/g,
													"",
												);
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.fatherName ? "is-invalid" : ""}`}
									/>
									{errors.fatherName && (
										<div className="invalid-feedback">
											{errors.fatherName.message as string}
										</div>
									)}
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Father's Aadhaar No
									</label>
									<input
										{...register("fatherAadhaar", {
											pattern: {
												value: /^[0-9]{12}$/,
												message: "Invalid 12-digit Aadhaar",
											},
										})}
										maxLength={12}
										disabled={isViewMode}
										className={`form-control ${errors.fatherAadhaar ? "is-invalid" : ""}`}
										placeholder="12-digit Aadhaar Number"
									/>
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Father's Occupation
									</label>
									<input
										{...register("fatherOccupation")}
										disabled={isViewMode}
										className="form-control"
										placeholder="Occupation"
									/>
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Father's Working Status
									</label>
									<select
										{...register("fatherWorkingStatus")}
										disabled={isViewMode}
										className="form-select"
									>
										<option value="None">None</option>
										<option value="Working">Working</option>
										<option value="Retired">Retired</option>
										<option value="Unemployed">Unemployed</option>
										<option value="Deceased">Deceased</option>
									</select>
								</div>

								{/* Mother Details */}
								<div className="col-md-3 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Mother's Name
									</label>
									<input
										{...register("motherName", {
											required: "Mother's Name is required",
											pattern: {
												value: /^[a-zA-Z\s]*$/,
												message: "Only alphabets are allowed",
											},
											onChange: (e) => {
												e.target.value = e.target.value.replace(
													/[^a-zA-Z\s]/g,
													"",
												);
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.motherName ? "is-invalid" : ""}`}
									/>
									{errors.motherName && (
										<div className="invalid-feedback">
											{errors.motherName.message as string}
										</div>
									)}
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Mother's Aadhaar No
									</label>
									<input
										{...register("motherAadhaar", {
											pattern: {
												value: /^[0-9]{12}$/,
												message: "Invalid 12-digit Aadhaar",
											},
										})}
										maxLength={12}
										disabled={isViewMode}
										className={`form-control ${errors.motherAadhaar ? "is-invalid" : ""}`}
										placeholder="12-digit Aadhaar Number"
									/>
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Mother's Occupation
									</label>
									<input
										{...register("motherOccupation")}
										disabled={isViewMode}
										className="form-control"
										placeholder="Occupation"
									/>
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Mother's Working Status
									</label>
									<select
										{...register("motherWorkingStatus")}
										disabled={isViewMode}
										className="form-select"
									>
										<option value="None">None</option>
										<option value="Working">Working</option>
										<option value="Home Maker">Home Maker</option>
										<option value="Retired">Retired</option>
										<option value="Unemployed">Unemployed</option>
										<option value="Deceased">Deceased</option>
									</select>
								</div>

								{/* Spouse Details */}
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Spouse Name
									</label>
									<input
										{...register("spouseName", {
											pattern: {
												value: /^[a-zA-Z\s]*$/,
												message: "Only alphabets are allowed",
											},
											onChange: (e) => {
												e.target.value = e.target.value.replace(
													/[^a-zA-Z\s]/g,
													"",
												);
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.spouseName ? "is-invalid" : ""}`}
									/>
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Spouse Aadhaar No
									</label>
									<input
										{...register("spouseAadhaar", {
											pattern: {
												value: /^[0-9]{12}$/,
												message: "Invalid 12-digit Aadhaar",
											},
										})}
										maxLength={12}
										disabled={isViewMode}
										className={`form-control ${errors.spouseAadhaar ? "is-invalid" : ""}`}
										placeholder="12-digit Aadhaar Number"
									/>
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Spouse Occupation
									</label>
									<input
										{...register("spouseOccupation")}
										disabled={isViewMode}
										className="form-control"
										placeholder="Occupation"
									/>
								</div>
								<div className="col-md-3 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Spouse Working Status
									</label>
									<select
										{...register("spouseWorkingStatus")}
										disabled={isViewMode}
										className="form-select"
									>
										<option value="None">None</option>
										<option value="Working">Working</option>
										<option value="Home Maker">Home Maker</option>
										<option value="Retired">Retired</option>
										<option value="Unemployed">Unemployed</option>
										<option value="Deceased">Deceased</option>
									</select>
								</div>
							</div>

							{/* Children Details */}
							<div className="border-top pt-5 mt-5">
								<div className="d-flex align-items-center justify-content-between mb-5">
									<h4 className="fw-bold text-dark mb-0">Children Details</h4>
									<div className="d-flex align-items-center gap-3">
										<label className="form-label fw-bold mb-0 text-dark me-2 fs-5">
											Number of Children:
										</label>
										<select
											className="form-select form-select-solid w-125px"
											value={childrenList.length}
											onChange={(e) =>
												handleNumChildrenChange(parseInt(e.target.value, 10))
											}
											disabled={isViewMode}
										>
											<option value="0">0 (None)</option>
											<option value="1">1 Child</option>
											<option value="2">2 Children</option>
											<option value="3">3 Children</option>
											<option value="4">4 Children</option>
											<option value="5">5 Children</option>
										</select>
										{!isViewMode && (
											<button
												type="button"
												className="btn btn-light-primary btn-sm ms-2"
												onClick={handleAddChild}
											>
												<i className="bi bi-plus-circle me-1" /> Add Child
											</button>
										)}
									</div>
								</div>

								{childrenList.length > 0 ? (
									<div className="row">
										{childrenList.map((child, idx) => (
											<div key={idx} className="col-12 mb-5">
												<div className="card card-dashed bg-light-primary p-5 position-relative">
													<div className="d-flex justify-content-between align-items-center mb-4">
														<h5 className="fw-bold text-primary mb-0">
															Child #{idx + 1} Information
														</h5>
														{!isViewMode && (
															<button
																type="button"
																className="btn btn-icon btn-sm btn-light-danger"
																onClick={() => handleRemoveChild(idx)}
																title="Remove Child"
															>
																<i className="bi bi-trash fs-6" />
															</button>
														)}
													</div>

													<div className="row">
														<div className="col-md-3 mb-3">
															<label className="form-label fw-bold fs-7">
																Child Name
															</label>
															<input
																type="text"
																className="form-control form-control-sm"
																value={child.name}
																onChange={(e) =>
																	handleChildChange(idx, "name", e.target.value)
																}
																placeholder="Enter Child Name"
																disabled={isViewMode}
															/>
														</div>

														<div className="col-md-3 mb-3">
															<label className="form-label fw-bold fs-7">
																Aadhaar Number
															</label>
															<input
																type="text"
																maxLength={12}
																className="form-control form-control-sm"
																value={child.aadhaar}
																onChange={(e) =>
																	handleChildChange(idx, "aadhaar", e.target.value)
																}
																placeholder="12-digit Aadhaar No"
																disabled={isViewMode}
															/>
														</div>

														<div className="col-md-2 mb-3">
															<label className="form-label fw-bold fs-7">
																Occupation
															</label>
															<input
																type="text"
																className="form-control form-control-sm"
																value={child.occupation}
																onChange={(e) =>
																	handleChildChange(
																		idx,
																		"occupation",
																		e.target.value,
																	)
																}
																placeholder="Occupation"
																disabled={isViewMode}
															/>
														</div>

														<div className="col-md-2 mb-3">
															<label className="form-label fw-bold fs-7">
																Working Status
															</label>
															<select
																className="form-select form-select-sm"
																value={child.workingStatus}
																onChange={(e) =>
																	handleChildChange(
																		idx,
																		"workingStatus",
																		e.target.value,
																	)
																}
																disabled={isViewMode}
															>
																<option value="Student">Student</option>
																<option value="Working">Working</option>
																<option value="Unemployed">Unemployed</option>
																<option value="N/A">N/A</option>
															</select>
														</div>

														<div className="col-md-2 mb-3">
															<label className="form-label fw-bold fs-7">
																Education Status
															</label>
															<select
																className="form-select form-select-sm"
																value={child.educationStatus}
																onChange={(e) =>
																	handleChildChange(
																		idx,
																		"educationStatus",
																		e.target.value,
																	)
																}
																disabled={isViewMode}
															>
																<option value="School">School</option>
																<option value="College">College</option>
																<option value="Completed">Completed</option>
																<option value="N/A">N/A</option>
															</select>
														</div>
													</div>
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-muted fs-7 italic mb-5">
										No children details added. Select Number of Children or click "+ Add Child" to fill children information.
									</div>
								)}
							</div>

							<h2 className="fw-bold mb-6 border-top border-bottom pt-5 pb-5">
								Personal Information
							</h2>

							<div className="row">
								<div className="col-md-4 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Contact Name
									</label>
									<input
										{...register("contactName", {
											required: "Contact Name is required",
											pattern: {
												value: /^[a-zA-Z\s]*$/,
												message: "Only alphabets are allowed",
											},
											onChange: (e) => {
												e.target.value = e.target.value.replace(
													/[^a-zA-Z\s]/g,
													"",
												);
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.contactName ? "is-invalid" : ""}`}
									/>
									{errors.contactName && (
										<div className="invalid-feedback">
											{errors.contactName.message as string}
										</div>
									)}
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Email ID
									</label>
									<input
										{...register("emailId", {
											required: "Email is required",
											pattern: {
												value:
													/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
												message: "Invalid email (Format: example@domain.com)",
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.emailId ? "is-invalid" : ""}`}
									/>
									{errors.emailId && (
										<div className="invalid-feedback">
											{errors.emailId.message as string}
										</div>
									)}
								</div>

								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										E-Portal Email ID
									</label>
									<input
										{...register("eportalEmail", {
											required: "E-Portal Email is required",
											pattern: {
												value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
												message: "Invalid email",
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.eportalEmail ? "is-invalid" : ""}`}
									/>
									{errors.eportalEmail && (
										<div className="invalid-feedback">
											{errors.eportalEmail.message as string}
										</div>
									)}
								</div>

								{/* PFMS ID Field hidden per request */}

								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Aadhaar Number
									</label>
									<input
										{...register("aadhaarNumber", {
											required: "Aadhaar Number is required",
											pattern: {
												value: /^[0-9]{12}$/,
												message: "Invalid 12-digit Aadhaar",
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.aadhaarNumber ? "is-invalid" : ""}`}
									/>
									{errors.aadhaarNumber && (
										<div className="invalid-feedback">
											{errors.aadhaarNumber.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<FileUpload
										label="Aadhaar File"
										value={watch("aadhaarFile")}
										required
										isInvalid={!!errors.aadhaarFile}
										disabled={isViewMode}
										onChange={(file) =>
											setValue("aadhaarFile", file, { shouldValidate: true })
										}
										onUpload={regApi.uploadAadhaarCard}
										maxSizeMB={5}
										description="Max 5MB (PDF/JPG/PNG)"
									/>
									{errors.aadhaarFile && (
										<div className="text-danger d-block mt-1 fw-bold fs-7">
											{errors.aadhaarFile.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										PAN Number
									</label>
									<input
										{...register("panNumber", {
											required: "PAN is required",
											pattern: {
												value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
												message: "Invalid PAN format",
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.panNumber ? "is-invalid" : ""}`}
									/>
									{errors.panNumber && (
										<div className="invalid-feedback">
											{errors.panNumber.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<FileUpload
										label="PAN File"
										value={watch("panFile")}
										required
										isInvalid={!!errors.panFile}
										disabled={isViewMode}
										onChange={(file) =>
											setValue("panFile", file, { shouldValidate: true })
										}
										onUpload={regApi.uploadPanCard}
										maxSizeMB={5}
										description="Max 5MB (PDF/JPG/PNG)"
									/>
									{errors.panFile && (
										<div className="text-danger d-block mt-1 fw-bold fs-7">
											{errors.panFile.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										UAN Number
									</label>
									<input
										{...register("uanNumber", {
											required: "UAN is required",
											pattern: {
												value: /^[0-9]{12}$/,
												message: "Invalid 12-digit UAN",
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.uanNumber ? "is-invalid" : ""}`}
									/>
									{errors.uanNumber && (
										<div className="invalid-feedback">
											{errors.uanNumber.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label fs-5 fw-bold text-dark">
										Sparsh No
									</label>
									<input
										{...register("sparshNo")}
										disabled={true}
										readOnly={true}
										className="form-control bg-light"
										placeholder="Sparsh No"
									/>
								</div>
							</div>

							<h2 className="fw-bold mb-6 border-top border-bottom pt-5 pb-5">
								Bank Details
							</h2>

							<div className="row">
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Account Holder Name
									</label>
									<input
										{...register("accountHolderName", {
											required: "Account Holder Name is required",
											validate: (value) => {
												if (typeof value !== "string")
													return "Account Holder Name must be a string";
												if (value.trim() === "")
													return "Account Holder Name cannot be empty";
												return true;
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.accountHolderName ? "is-invalid" : ""}`}
										placeholder="Enter Account Holder Name"
									/>
									{errors.accountHolderName && (
										<div className="invalid-feedback">
											{errors.accountHolderName.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Bank Name
									</label>
									<input
										{...register("bankName", {
											required: "Bank Name is required",
										})}
										disabled={isViewMode}
										className={`form-control ${errors.bankName ? "is-invalid" : ""}`}
									/>
									{errors.bankName && (
										<div className="invalid-feedback">
											{errors.bankName.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Branch Name
									</label>
									<input
										{...register("branchName", {
											required: "Branch Name is required",
										})}
										disabled={isViewMode}
										className={`form-control ${errors.branchName ? "is-invalid" : ""}`}
									/>
									{errors.branchName && (
										<div className="invalid-feedback">
											{errors.branchName.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Account Number
									</label>
									<input
										type="text"
										{...register("accountNumber", {
											required: "Account Number is required",
											pattern: isSelfProfile ? {
												value: /^[0-9]+$/,
												message: "Invalid Account Number",
											} : undefined,
										})}
										disabled={isViewMode || !isSelfProfile}
										className={`form-control ${errors.accountNumber ? "is-invalid" : ""}`}
										placeholder="Enter Account Number"
									/>
									{errors.accountNumber && (
										<div className="invalid-feedback d-block">
											{errors.accountNumber.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Retype Account No
									</label>
									<input
										type="text"
										{...register("retypeAccountNumber", {
											required: "Retype Account No is required",
											validate: (value) => {
												if (!isSelfProfile) return true;
												if (typeof value !== "string")
													return "Retype Account No must be a string";
												if (value.trim() === "")
													return "Retype Account No cannot be empty";
												if (value !== watch("accountNumber"))
													return "Account Numbers do not match";
												return true;
											},
										})}
										disabled={isViewMode || !isSelfProfile}
										className={`form-control ${errors.retypeAccountNumber ? "is-invalid" : ""}`}
										placeholder="Retype Account Number"
										autoComplete="off"
										onPaste={(e) => {
											e.preventDefault();
											toast.error("Copy and paste is disabled for Retype Account Number.");
										}}
										onCopy={(e) => e.preventDefault()}
										onCut={(e) => e.preventDefault()}
										onDrop={(e) => e.preventDefault()}
									/>
									{errors.retypeAccountNumber && (
										<div className="invalid-feedback d-block">
											{errors.retypeAccountNumber.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										IFSC Code
									</label>
									<input
										{...register("ifsc", {
											required: "IFSC is required",
											pattern: {
												value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
												message: "Invalid IFSC (e.g. ABCD0123456)",
											},
										})}
										disabled={isViewMode}
										className={`form-control ${errors.ifsc ? "is-invalid" : ""}`}
									/>
									{errors.ifsc && (
										<div className="invalid-feedback">
											{errors.ifsc.message as string}
										</div>
									)}
								</div>
								<div className="col-md-4 mb-5">
									<FileUpload
										label="Bank Passbook File"
										value={watch("bankPassbookFile")}
										required
										isInvalid={!!errors.bankPassbookFile}
										disabled={isViewMode}
										onChange={(file) =>
											setValue("bankPassbookFile", file, {
												shouldValidate: true,
											})
										}
										maxSizeMB={5}
										onUpload={regApi.uploadBankPassbook}
										description="Max 5MB (PDF/JPG/PNG)"
									/>
									{errors.bankPassbookFile && (
										<div className="text-danger d-block mt-1 fw-bold fs-7">
											{errors.bankPassbookFile.message as string}
										</div>
									)}
								</div>
							</div>
						</>
					)}

					{/* ================= STEP 3 ================= */}
					{currentStep === 3 && (
						<>
							{/* ================= ACADEMIC DETAILS ================= */}
							<div className="mt-10">
								<div className="d-flex justify-content-between align-items-center mb-6 border-bottom border-top py-4">
									<h4 className="fw-bold mb-0">Academic Details</h4>

									{!isViewMode && (
										<button
											type="button"
											className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
											onClick={addAcademic}
										>
											<CirclePlus size={17} /> Add Academic Record
										</button>
									)}
								</div>
								<div className="table-responsive">
									<table className="table table-striped table-bordered align-middle">
										<thead>
											<tr>
												<th>Level</th>
												<th>Course</th>
												<th>Institution</th>
												<th>Board / University</th>
												<th>Year From</th>
												<th>Year To</th>
												<th>Aggregate %</th>
												<th>Certificate File </th>
												{!isViewMode && <th>Action</th>}
											</tr>
										</thead>

										<tbody>
											{academicList.map((item, index) => (
												<tr key={index}>
													<td>
														<select
															className="form-select"
															style={{ minWidth: "150px" }}
															value={item.level}
															disabled={isViewMode}
															onChange={(e) =>
																updateAcademicDetail(
																	index,
																	"level",
																	e.target.value,
																)
															}
														>
															<option value="">Select Level</option>
															{levelList.map((item) => (
																<option
																	key={item.id}
																	value={item.id.toString()}
																>
																	{item.name}
																</option>
															))}
														</select>
													</td>

													<td>
														{(() => {
															const availableCourses = item.level
																? courseList.filter(
																		(c: any) =>
																			String(c.level_id) === String(item.level),
																	)
																: courseList;

															return (
																<select
																	className="form-select"
																	style={{ minWidth: "180px" }}
																	value={item.course}
																	disabled={isViewMode || !item.level}
																	onChange={(e) =>
																		updateAcademicDetail(
																			index,
																			"course",
																			e.target.value,
																		)
																	}
																>
																	<option value="">
																		{!item.level
																			? "Select Level First"
																			: availableCourses.length === 0
																				? "No Courses Available"
																				: "Select Course"}
																	</option>
																	{availableCourses.map((c: any) => (
																		<option key={c.id} value={c.name}>
																			{c.name}
																		</option>
																	))}
																	{item.course &&
																		!availableCourses.some(
																			(c: any) =>
																				c.name.toLowerCase() ===
																				item.course.toLowerCase(),
																		) && <option value={item.course}>{item.course}</option>}
																</select>
															);
														})()}
													</td>

													<td>
														<input
															className="form-control"
															style={{ minWidth: "200px" }}
															value={item.institution}
															disabled={isViewMode}
															onChange={(e) =>
																updateAcademicDetail(
																	index,
																	"institution",
																	e.target.value,
																)
															}
														/>
													</td>

													<td>
														<input
															className="form-control"
															style={{ minWidth: "200px" }}
															value={item.board}
															disabled={isViewMode}
															onChange={(e) =>
																updateAcademicDetail(
																	index,
																	"board",
																	e.target.value,
																)
															}
														/>
													</td>

													<td>
														<select
															className="form-select"
															style={{ minWidth: "120px" }}
															value={item.from}
															disabled={isViewMode}
															onChange={(e) =>
																updateAcademicDetail(
																	index,
																	"from",
																	e.target.value,
																)
															}
														>
															<option value="">Year</option>
															{[...Array(50)].map((_, i) => (
																<option key={i} value={(2024 - i).toString()}>
																	{2024 - i}
																</option>
															))}
														</select>
													</td>

													<td>
														<select
															className="form-select"
															style={{ minWidth: "120px" }}
															value={item.to}
															disabled={isViewMode}
															onChange={(e) =>
																updateAcademicDetail(
																	index,
																	"to",
																	e.target.value,
																)
															}
														>
															<option value="">Year</option>
															{[...Array(50)].map((_, i) => (
																<option key={i} value={(2024 - i).toString()}>
																	{2024 - i}
																</option>
															))}
														</select>
													</td>

													<td>
														<input
															className="form-control"
															style={{ minWidth: "100px" }}
															placeholder="%"
															value={item.percentage}
															disabled={isViewMode}
															onChange={(e) =>
																updateAcademicDetail(
																	index,
																	"percentage",
																	e.target.value,
																)
															}
														/>
													</td>

													<td>
														<div style={{ minWidth: "300px" }}>
															<FileUpload
																label=""
																value={item.certificate as any}
																disabled={isViewMode}
																onChange={(file) =>
																	updateAcademicDetail(
																		index,
																		"certificate",
																		file as any,
																	)
																}
																onUpload={regApi.uploadAcademicCertificate}
																maxSizeMB={5}
																description="Max 5MB (PDF/JPG/PNG)"
															/>
														</div>
													</td>

													{!isViewMode && (
														<td>
															{academicList.length > 1 && (
																<button
																	type="button"
																	className="btn btn-sm btn-danger"
																	onClick={() => removeAcademic(index)}
																>
																	<Trash2 size={16} />
																</button>
															)}
														</td>
													)}
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
							{/* ================= WORK EXPERIENCE ================= */}
							<div className="mt-20">
								<div className="d-flex justify-content-between align-items-center mb-6 border-bottom border-top py-4">
									<h4 className="fw-bold mb-0">Work Experience</h4>
									{!isViewMode && (
										<button
											type="button"
											className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
											onClick={addExperience}
										>
											<CirclePlus size={17} /> Add Work Experience
										</button>
									)}
								</div>
								<div className="table-responsive">
									<table className="table table-striped table-bordered align-middle">
										<thead>
											<tr>
												<th>Company</th>
												<th>Nature of Job</th>
												<th>Work From</th>
												<th>Work To</th>
												<th>Duration</th>
												<th>Experience File</th>
												{!isViewMode && <th>Action</th>}
											</tr>
										</thead>

										<tbody>
											{experienceList.map((item, index) => (
												<tr key={index}>
													<td>
														<input
															className="form-control"
															style={{ minWidth: "150px" }}
															value={item.company}
															disabled={isViewMode}
															onChange={(e) =>
																updateExperienceDetail(
																	index,
																	"company",
																	e.target.value,
																)
															}
														/>
													</td>

													<td>
														<input
															className="form-control"
															style={{ minWidth: "150px" }}
															value={item.jobNature}
															disabled={isViewMode}
															onChange={(e) =>
																updateExperienceDetail(
																	index,
																	"jobNature",
																	e.target.value,
																)
															}
														/>
													</td>

													<td>
														<input
															type="date"
															className="form-control"
															style={{ minWidth: "150px" }}
															value={item.from}
															max={today}
															disabled={isViewMode}
															onChange={(e) =>
																updateExperienceDetail(
																	index,
																	"from",
																	e.target.value,
																)
															}
														/>
													</td>

													<td>
														<input
															type="date"
															className="form-control"
															style={{ minWidth: "150px" }}
															value={item.to}
															max={today}
															disabled={isViewMode}
															onChange={(e) =>
																updateExperienceDetail(
																	index,
																	"to",
																	e.target.value,
																)
															}
														/>
													</td>

													<td>
														<input
															className="form-control bg-light"
															style={{ minWidth: "120px" }}
															value={item.duration}
															readOnly
															disabled={isViewMode}
														/>
													</td>

													<td>
														<div style={{ minWidth: "300px" }}>
															<FileUpload
																label=""
																value={item.certificate as any}
																disabled={isViewMode}
																onChange={(file) =>
																	updateExperienceDetail(
																		index,
																		"certificate",
																		file as any,
																	)
																}
																onUpload={regApi.uploadExperienceCertificate}
																maxSizeMB={5}
																description="Max 5MB (PDF/JPG/PNG)"
															/>
														</div>
													</td>

													{!isViewMode && (
														<td>
															{experienceList.length > 1 && (
																<button
																	type="button"
																	className="btn btn-sm btn-danger"
																	onClick={() => removeExperience(index)}
																>
																	<Trash2 size={16} />
																</button>
															)}
														</td>
													)}
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
							{/* ================= SKILLS ================= */}
							<div className="mt-20">
								<div className="d-flex justify-content-between align-items-center mb-6 border-bottom border-top py-4">
									<h4 className="fw-bold mb-0">Skills</h4>

									{!isViewMode && (
										<button
											type="button"
											className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
											onClick={addSkill}
										>
											<CirclePlus size={17} /> Add Skill
										</button>
									)}
								</div>

								<table className="table table-striped table-bordered align-middle">
									<thead>
										<tr>
											<th>Skill</th>
											{!isViewMode && <th>Action</th>}
										</tr>
									</thead>

									<tbody>
										{skillList.map((item, index) => (
											<tr key={index}>
												<td>
													<input
														className="form-control"
														placeholder="Enter Skill"
														value={item.skill}
														onChange={(e) =>
															updateSkillDetail(index, e.target.value)
														}
													/>
												</td>
												{!isViewMode && (
													<td>
														{skillList.length > 1 && (
															<button
																type="button"
																className="btn btn-sm btn-danger"
																onClick={() => removeSkill(index)}
															>
																<Trash2 size={16} />
															</button>
														)}
													</td>
												)}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</>
					)}
					{/* ================= STEP 4 ================= */}
					{currentStep === 4 && isSpecialRole && (
						<>
							<h4 className="fw-bold mb-6 border-bottom">Working Details</h4>

							<div className="row">
								<div className="col-md-4 mb-5">
									<label className="form-label required fs-5 fw-bold text-dark">
										Joining Date
									</label>
									<input
										type="date"
										max={today}
										{...register("joining_date", {
											required: "Joining Date is required",
										})}
										className={`form-control ${errors.joining_date ? "is-invalid" : ""}`}
									/>
									{errors.joining_date && (
										<div className="invalid-feedback">
											{errors.joining_date.message as string}
										</div>
									)}
								</div>
							</div>

							<div className="d-flex justify-content-between align-items-center mb-6">
								<h5 className="fw-bold mb-0">
									{roleDesignation === "3"
										? "BRP Working Info"
										: "DRP Working Info"}
								</h5>
								{!isViewMode && (
									<button
										type="button"
										className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
										onClick={addWorkingInfo}
									>
										<CirclePlus size={17} /> Add Station
									</button>
								)}
							</div>

							<div className="table-responsive">
								<table className="table table-striped table-bordered align-middle">
									<thead>
										<tr>
											<th>Station</th>
											<th>District</th>
											<th>Block</th>
											<th>From</th>
											<th>To</th>
											<th>Duration</th>
											{!isViewMode && <th>Action</th>}
										</tr>
									</thead>

									<tbody>
										{workingInfoList.map((item, index) => (
											<tr key={index}>
												<td>
													<select
														className="form-control"
														value={item.station}
														disabled={isViewMode}
														onChange={(e) =>
															updateWorkingDetail(
																index,
																"station",
																e.target.value,
															)
														}
													>
														<option>Worked</option>
														<option>Present</option>
													</select>
												</td>

												<td>
													<select
														className="form-control"
														value={item.district}
														disabled={isViewMode}
														onChange={(e) =>
															updateWorkingDetail(
																index,
																"district",
																e.target.value,
															)
														}
													>
														<option value="">Select District</option>
														{districtList.map((d) => (
															<option key={d.id} value={d.id.toString()}>
																{d.name}
															</option>
														))}
													</select>
												</td>

												<td>
													<select
														className="form-control"
														value={item.block}
														disabled={isViewMode}
														onChange={(e) =>
															updateWorkingDetail(
																index,
																"block",
																e.target.value,
															)
														}
													>
														<option value="">Select Block</option>
														{(item.blocks || []).map((b) => (
															<option key={b.id} value={b.id.toString()}>
																{b.name}
															</option>
														))}
													</select>
												</td>

												<td>
													<input
														type="date"
														className="form-control"
														disabled={isViewMode}
														max={today}
														value={item.from}
														onChange={(e) =>
															updateWorkingDetail(index, "from", e.target.value)
														}
													/>
												</td>

												<td>
													<input
														type="date"
														className="form-control"
														disabled={isViewMode || item.station === "Present"}
														max={today}
														value={item.station === "Present" ? today : item.to}
														onChange={(e) =>
															updateWorkingDetail(index, "to", e.target.value)
														}
													/>
												</td>

												<td>
													<input
														className="form-control bg-light"
														value={
															item.station === "Present"
																? calculateMonths(item.from, today)
																: item.duration
														}
														disabled={isViewMode}
														readOnly
													/>
												</td>

												{!isViewMode && (
													<td>
														{workingInfoList.length > 1 && (
															<button
																type="button"
																className="btn btn-sm btn-danger"
																onClick={() => removeWorkingInfo(index)}
															>
																<Trash2 size={16} />
															</button>
														)}
													</td>
												)}
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{roleDesignation === "3" && (
								<>
									<div className="mt-10 mb-7">
										<label className="form-label required d-block mb-3 fw-bold">
											Have you worked as DRP I/C?
										</label>
										<div className="d-flex gap-10">
											<div className="form-check form-check-custom form-check-solid">
												<input
													className="form-check-input"
													type="radio"
													value="yes"
													{...register("hasWorkedAsDrpIc")}
													id="drpic_yes"
												/>
												<label
													className="form-check-label ms-3"
													htmlFor="drpic_yes"
												>
													Yes
												</label>
											</div>
											<div className="form-check form-check-custom form-check-solid">
												<input
													className="form-check-input"
													type="radio"
													value="no"
													{...register("hasWorkedAsDrpIc")}
													id="drpic_no"
												/>
												<label
													className="form-check-label ms-3"
													htmlFor="drpic_no"
												>
													No
												</label>
											</div>
										</div>
									</div>

									{hasWorkedAsDrpIc === "yes" && (
										<>
											<div className="d-flex justify-content-between align-items-center mb-6">
												<h5 className="fw-bold mb-0">DRP I/C Working Info</h5>
												{!isViewMode && (
													<button
														type="button"
														className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
														onClick={addDrpIcWorkingInfo}
													>
														<CirclePlus size={17} /> Add Station
													</button>
												)}
											</div>

											<div className="table-responsive">
												<table className="table table-striped table-bordered align-middle">
													<thead>
														<tr>
															<th>Station</th>
															<th>District</th>
															<th>Block</th>
															<th>From</th>
															<th>To</th>
															<th>Duration</th>
															{!isViewMode && <th>Action</th>}
														</tr>
													</thead>

													<tbody>
														{drpIcWorkingInfoList.map((item, index) => (
															<tr key={index}>
																<td>
																	<select
																		className="form-control"
																		value={item.station}
																		disabled={isViewMode}
																		onChange={(e) =>
																			updateDrpIcWorkingDetail(
																				index,
																				"station",
																				e.target.value,
																			)
																		}
																	>
																		<option>Worked</option>
																		<option>Present</option>
																	</select>
																</td>

																<td>
																	<select
																		className="form-control"
																		value={item.district}
																		disabled={isViewMode}
																		onChange={(e) =>
																			updateDrpIcWorkingDetail(
																				index,
																				"district",
																				e.target.value,
																			)
																		}
																	>
																		<option value="">Select District</option>
																		{districtList.map((d) => (
																			<option
																				key={d.id}
																				value={d.id.toString()}
																			>
																				{d.name}
																			</option>
																		))}
																	</select>
																</td>

																<td>
																	<select
																		className="form-control"
																		value={item.block}
																		disabled={isViewMode}
																		onChange={(e) =>
																			updateDrpIcWorkingDetail(
																				index,
																				"block",
																				e.target.value,
																			)
																		}
																	>
																		<option value="">Select Block</option>
																		{(item.blocks || []).map((b) => (
																			<option
																				key={b.id}
																				value={b.id.toString()}
																			>
																				{b.name}
																			</option>
																		))}
																	</select>
																</td>

																<td>
																	<input
																		type="date"
																		className="form-control"
																		max={today}
																		value={item.from}
																		disabled={isViewMode}
																		onChange={(e) =>
																			updateDrpIcWorkingDetail(
																				index,
																				"from",
																				e.target.value,
																			)
																		}
																	/>
																</td>

																<td>
																	<input
																		type="date"
																		className="form-control"
																		max={today}
																		value={
																			item.station === "Present"
																				? today
																				: item.to
																		}
																		disabled={
																			isViewMode || item.station === "Present"
																		}
																		onChange={(e) =>
																			updateDrpIcWorkingDetail(
																				index,
																				"to",
																				e.target.value,
																			)
																		}
																	/>
																</td>

																<td>
																	<input
																		className="form-control bg-light"
																		value={
																			item.station === "Present"
																				? calculateMonths(item.from, today)
																				: item.duration
																		}
																		readOnly
																		disabled={isViewMode}
																	/>
																</td>

																{!isViewMode && (
																	<td>
																		{drpIcWorkingInfoList.length > 1 && (
																			<button
																				type="button"
																				className="btn btn-sm btn-danger"
																				onClick={() =>
																					removeDrpIcWorkingInfo(index)
																				}
																			>
																				<Trash2 size={16} />
																			</button>
																		)}
																	</td>
																)}
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</>
									)}
								</>
							)}
						</>
					)}
					{/* ================= STEP 5 ================= */}
					{currentStep === 5 && isSpecialRole && (
						<>
							<h4 className="fw-bold mb-6 border-bottom">
								Training & Pilot Audit Particulars
							</h4>

							{/* Tabs */}

							<div className="btn-group w-100 mb-8 p-1 bg-light rounded shadow-sm">
								<button
									type="button"
									className={`btn btn-sm flex-fill fw-bold py-3 transition-all ${trainingTab === "taken" ? "btn-primary text-white shadow-sm" : "btn-hover-light-primary text-gray-600"}`}
									onClick={() => setTrainingTab("taken")}
								>
									Training Taken
								</button>
								<button
									type="button"
									className={`btn btn-sm flex-fill fw-bold py-3 transition-all ${trainingTab === "given" ? "btn-primary text-white shadow-sm" : "btn-hover-light-primary text-gray-600"}`}
									onClick={() => setTrainingTab("given")}
								>
									Training Given
								</button>
								<button
									type="button"
									className={`btn btn-sm flex-fill fw-bold py-3 transition-all ${trainingTab === "audit" ? "btn-primary text-white shadow-sm" : "btn-hover-light-primary text-gray-600"}`}
									onClick={() => setTrainingTab("audit")}
								>
									Pilot Audit
								</button>
								<button
									type="button"
									className={`btn btn-sm flex-fill fw-bold py-3 transition-all ${trainingTab === "office" ? "btn-primary text-white shadow-sm" : "btn-hover-light-primary text-gray-600"}`}
									onClick={() => setTrainingTab("office")}
								>
									State Office Activities
								</button>
							</div>

							{/* TRAINING TAKEN */}

							{trainingTab === "taken" && (
								<div className="mt-4 animate__animated animate__fadeIn">
									<div className="d-flex justify-content-between align-items-center mb-6">
										<h5 className="fw-bold mb-0">Training Taken Records</h5>
										{!isViewMode && (
											<button
												type="button"
												className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
												onClick={addTrainingTaken}
											>
												<CirclePlus size={17} /> Add Record
											</button>
										)}
									</div>
									<div className="table-responsive">
										<table className="table table-striped table-bordered align-middle">
											<thead>
												<tr>
													<th>From</th>
													<th>To</th>
													<th>Duration</th>
													<th>Location</th>
													<th>Training Name</th>
													<th>Grade</th>
													{!isViewMode && <th>Action</th>}
												</tr>
											</thead>
											<tbody>
												{trainingTakenList.map((item, index) => (
													<tr key={index}>
														<td>
															<input
																type="date"
																className="form-control"
																value={item.from}
																max={today}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingTaken(
																		index,
																		"from",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																type="date"
																className="form-control"
																value={item.to}
																max={today}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingTaken(
																		index,
																		"to",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.duration}
																readOnly
																disabled={isViewMode}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.location}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingTaken(
																		index,
																		"location",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.name}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingTaken(
																		index,
																		"name",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.grade}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingTaken(
																		index,
																		"grade",
																		e.target.value,
																	)
																}
															/>
														</td>
														{!isViewMode && (
															<td>
																{trainingTakenList.length > 1 && (
																	<button
																		type="button"
																		className="btn btn-sm btn-danger"
																		onClick={() => removeTrainingTaken(index)}
																	>
																		<Trash2 size={16} />
																	</button>
																)}
															</td>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{/* TRAINING GIVEN */}

							{trainingTab === "given" && (
								<div className="mt-4 animate__animated animate__fadeIn">
									<div className="d-flex justify-content-between align-items-center mb-6">
										<h5 className="fw-bold mb-0">Training Given Records</h5>
										{!isViewMode && (
											<button
												type="button"
												className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
												onClick={addTrainingGiven}
											>
												<CirclePlus size={17} /> Add Record
											</button>
										)}
									</div>
									<div className="table-responsive">
										<table className="table table-striped table-bordered align-middle">
											<thead>
												<tr>
													<th>From</th>
													<th>To</th>
													<th>Duration</th>
													<th>Location</th>
													<th>Training Name</th>
													{!isViewMode && <th>Action</th>}
												</tr>
											</thead>
											<tbody>
												{trainingGivenList.map((item, index) => (
													<tr key={index}>
														<td>
															<input
																type="date"
																className="form-control"
																value={item.from}
																max={today}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingGiven(
																		index,
																		"from",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																type="date"
																className="form-control"
																value={item.to}
																max={today}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingGiven(
																		index,
																		"to",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.duration}
																disabled={isViewMode}
																readOnly
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.location}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingGiven(
																		index,
																		"location",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.name}
																disabled={isViewMode}
																onChange={(e) =>
																	updateTrainingGiven(
																		index,
																		"name",
																		e.target.value,
																	)
																}
															/>
														</td>
														{!isViewMode && (
															<td>
																{trainingGivenList.length > 1 && (
																	<button
																		type="button"
																		className="btn btn-sm btn-danger"
																		onClick={() => removeTrainingGiven(index)}
																	>
																		<Trash2 size={16} />
																	</button>
																)}
															</td>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{/* PILOT AUDIT */}

							{trainingTab === "audit" && (
								<div className="mt-4 animate__animated animate__fadeIn">
									<div className="d-flex justify-content-between align-items-center mb-6">
										<h5 className="fw-bold mb-0">Pilot Audit Records</h5>
										{!isViewMode && (
											<button
												type="button"
												className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
												onClick={addPilotAudit}
											>
												<CirclePlus size={17} /> Add Record
											</button>
										)}
									</div>
									<div className="table-responsive">
										<table className="table table-striped table-bordered align-middle">
											<thead>
												<tr>
													<th>From</th>
													<th>To</th>
													<th>Duration</th>
													<th>Location</th>
													<th>Scheme Name</th>
													{!isViewMode && <th>Action</th>}
												</tr>
											</thead>
											<tbody>
												{pilotAuditList.map((item, index) => (
													<tr key={index}>
														<td>
															<input
																type="date"
																className="form-control"
																value={item.from}
																max={today}
																disabled={isViewMode}
																onChange={(e) =>
																	updatePilotAudit(
																		index,
																		"from",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																type="date"
																className="form-control"
																value={item.to}
																max={today}
																disabled={isViewMode}
																onChange={(e) =>
																	updatePilotAudit(index, "to", e.target.value)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.duration}
																disabled={isViewMode}
																readOnly
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.location}
																disabled={isViewMode}
																onChange={(e) =>
																	updatePilotAudit(
																		index,
																		"location",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<select
																className="form-control"
																value={item.scheme}
																disabled={isViewMode}
																onChange={(e) =>
																	updatePilotAudit(
																		index,
																		"scheme",
																		e.target.value,
																	)
																}
															>
																<option value="">Select Scheme</option>
																{schemeList.map((s) => (
																	<option key={s.id} value={s.id.toString()}>
																		{s.name}
																	</option>
																))}
															</select>
														</td>
														{!isViewMode && (
															<td>
																{pilotAuditList.length > 1 && (
																	<button
																		type="button"
																		className="btn btn-sm btn-danger"
																		onClick={() => removePilotAudit(index)}
																	>
																		<Trash2 size={16} />
																	</button>
																)}
															</td>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{/* STATE OFFICE ACTIVITIES */}

							{trainingTab === "office" && (
								<div className="mt-4 animate__animated animate__fadeIn">
									<div className="d-flex justify-content-between align-items-center mb-6">
										<h5 className="fw-bold mb-0">State Office Activities</h5>
										{!isViewMode && (
											<button
												type="button"
												className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
												onClick={addStateOffice}
											>
												<CirclePlus size={17} /> Add Activity
											</button>
										)}
									</div>
									{stateOfficeList.map((item, index) => (
										<div className="row mb-5" key={index}>
											<div className="col-md-3 mb-5">
												<label className="form-label fs-5 fw-bold text-dark">
													Year
												</label>
												<input
													type="number"
													className="form-control"
													value={item.year}
													disabled={isViewMode}
													onChange={(e) =>
														updateStateOfficeDetail(
															index,
															"year",
															e.target.value,
														)
													}
												/>
											</div>
											<div className="col-md-7 mb-5">
												<label className="form-label fs-5 fw-bold text-dark">
													Work Particulars
												</label>
												<textarea
													className="form-control"
													value={item.workParticulars}
													disabled={isViewMode}
													onChange={(e) =>
														updateStateOfficeDetail(
															index,
															"workParticulars",
															e.target.value,
														)
													}
												/>
											</div>
											{!isViewMode && (
												<div className="col-md-2 mb-5 d-flex align-items-end">
													{stateOfficeList.length > 1 && (
														<button
															type="button"
															className="btn btn-sm btn-danger mb-1"
															onClick={() => removeStateOffice(index)}
														>
															<Trash2 size={16} />
														</button>
													)}
												</div>
											)}
										</div>
									))}
								</div>
							)}

							{/* ================= COMPLAINTS ================= */}

							{/* <h4 className="fw-bold mt-10 mb-6 border-bottom">Complaints</h4> */}

							{/* Tabs */}

							{/* <div className="btn-group mb-6 p-1 bg-light rounded shadow-sm">
								<button
									type="button"
									className={`btn btn-sm flex-fill fw-bold py-3 transition-all ${complainTab === "Complaints" ? "btn-primary text-white shadow-sm" : "btn-hover-light-primary text-gray-600"}`}
									onClick={() => setComplainTab("Complaints")}
								>
									Complaints
								</button>

								<button
									type="button"
									className={`btn btn-sm flex-fill fw-bold py-3 transition-all ${complainTab === "Others" ? "btn-primary text-white shadow-sm" : "btn-hover-light-primary text-gray-600"}`}
									onClick={() => setComplainTab("Others")}
								>
									Others
								</button>
							</div>
							{complainTab === "Complaints" && (
								<div className="animate__animated animate__fadeIn">
									<div className="d-flex justify-content-between align-items-center mt-4 mb-6 border-bottom">
										<h4 className="fw-bold mb-0">Complaints</h4>
										{!isViewMode && (
											<button
												type="button"
												className="btn btn-sm btn-light-primary d-flex align-items-center gap-2 mb-2"
												onClick={addComplaint}
											>
												<CirclePlus size={17} /> Add Complaint
											</button>
										)}
									</div>
									<div className="table-responsive">
										<table className="table table-striped table-bordered align-middle">
											<thead>
												<tr>
													<th>Received On</th>
													<th>Name & Contact</th>
													<th>Complaint</th>
													<th>Remarks</th>
													<th>Action Taken</th>
													{!isViewMode && <th>Action</th>}
												</tr>
											</thead>
											<tbody>
												{complaintList.map((item, index) => (
													<tr key={index}>
														<td>
															<input
																type="date"
																className="form-control"
																value={item.receivedOn}
																disabled={isViewMode}
																onChange={(e) =>
																	updateComplaintDetail(
																		index,
																		"receivedOn",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.complainantName}
																disabled={isViewMode}
																onChange={(e) =>
																	updateComplaintDetail(
																		index,
																		"complainantName",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.complaint}
																disabled={isViewMode}
																onChange={(e) =>
																	updateComplaintDetail(
																		index,
																		"complaint",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.remarks}
																disabled={isViewMode}
																onChange={(e) =>
																	updateComplaintDetail(
																		index,
																		"remarks",
																		e.target.value,
																	)
																}
															/>
														</td>
														<td>
															<input
																className="form-control"
																value={item.actionTaken}
																disabled={isViewMode}
																onChange={(e) =>
																	updateComplaintDetail(
																		index,
																		"actionTaken",
																		e.target.value,
																	)
																}
															/>
														</td>
														{!isViewMode && (
															<td>
																{complaintList.length > 1 && (
																	<button
																		type="button"
																		className="btn btn-sm btn-danger"
																		onClick={() => removeComplaint(index)}
																	>
																		<Trash2 size={16} />
																	</button>
																)}
															</td>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}
							{complainTab === "Others" && (
								<>
									<div className="d-flex justify-content-between align-items-center mt-4">
										<h6 className="fw-semibold text-white bg-danger p-2 rounded mb-0">
											This Section is under Construction
										</h6>
									</div>
								</>
							)} */}
						</>
					)}

					{/* ================= BUTTONS ================= */}
					<div className="d-flex justify-content-between mt-10 pt-5 border-top">
						{currentStep > 0 && (
							<button
								type="button"
								className="btn me-3 grayButtonStyle"
								onClick={prevStep}
							>
								Back
							</button>
						)}

						{currentStep < steps.length && (
							<button
								key="btn-continue"
								type="button"
								className="btn btn-primary"
								onClick={nextStep}
								disabled={loading}
							>
								{isViewMode
									? "Next"
									: loading
										? "Saving..."
										: "Save and Continue"}
							</button>
						)}

						{currentStep === steps.length && (
							<button
								key="btn-submit"
								type="button"
								className="btn btn-success fw-bold px-10"
								disabled={loading}
								onClick={onSubmit}
							>
								{isViewMode
									? "Finish"
									: loading
										? "Submitting..."
										: "Submit Registration"}
							</button>
						)}
					</div>
				</form>
			</div>
		</div>
	);
};

export default RegistrationFormPage;
